/* eslint-disable no-unused-vars */
import { useState, useEffect, useRef } from "react";
import { axiosInstance, API } from "../App";
import { useBox } from "../components/Layout";
import { CameraScanButton, PriceCheckButton } from "../components/CameraScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Package, Plus, Search, Trash2, Edit, ShoppingCart, Download, Upload, Barcode, AlertTriangle, ArrowUp, ArrowDown, Printer, Settings, FileText, Receipt, BarChart3, XCircle, Calendar, TrendingUp, TrendingDown, ClipboardList, Clock, Boxes, MinusCircle, Gift, Eye, EyeOff, MessageCircle, CheckCircle2, RotateCcw } from "lucide-react";
import { getErrorMessage } from "../utils/errorHandler";
import { printHtmlInWindow } from "../utils/printHelper";
import { todayCR } from "../utils/dateHelpers";
import InventoryAudit from "./InventoryAudit";

export default function Inventory({ user }) {
  const { selectedBox } = useBox();
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["productos", "vender", "ventas", "reportes", "auditoria"].includes(t) ? t : "productos";
  });
  
  // Active cash register for accounting
  const [activeCashRegister, setActiveCashRegister] = useState(null);
  
  // Dialogs
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSaleDialog, setShowSaleDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  // Bulk image upload to existing products (matches filename to product code).
  const bulkImagesInputRef = useRef(null);
  const [bulkImagesResult, setBulkImagesResult] = useState(null);
  // Import history dialog (Master only): list of past Excel uploads with
  // ability to download a report or revert the entire upload.
  const [showImportHistory, setShowImportHistory] = useState(false);
  const [importBatches, setImportBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchActionId, setBatchActionId] = useState(null);  // ID of batch being acted on (reverting/downloading)

  // Withdrawal dialog (MASTER only): regalia / retiro propietario / dano / otro.
  // Decrements stock + records cost; does NOT affect cash register.
  const [showWithdrawalDialog, setShowWithdrawalDialog] = useState(false);
  const [withdrawalForm, setWithdrawalForm] = useState({
    product_id: "",
    quantity: 1,
    reason: "regalia",
    notes: "",
  });
  const [withdrawalLoading, setWithdrawalLoading] = useState(false);
  const [withdrawalReceipt, setWithdrawalReceipt] = useState(null);
  const [withdrawalItems, setWithdrawalItems] = useState([]); // multiple items en un solo comprobante

  // Nota de Crédito parcial (devolución)
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnSale, setReturnSale] = useState(null); // full sale object being partially returned
  const [returnQuantities, setReturnQuantities] = useState({}); // product_id -> qty
  const [returnReason, setReturnReason] = useState("");
  const [returnLoading, setReturnLoading] = useState(false);

  // Datáfono (POS de tarjeta)
  const [datafonos, setDatafonos] = useState([]);
  const [selectedDatafonoId, setSelectedDatafonoId] = useState("");
  const [datafonoReference, setDatafonoReference] = useState("");
  const [cardType, setCardType] = useState("credito"); // "debito" | "credito"
  
  // Selected items
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  
  // Quick product lookup
  const [lookupCode, setLookupCode] = useState("");
  const [lookupProduct, setLookupProduct] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  
  // Cart for sales
  const [cart, setCart] = useState([]);
  const [pendingOnlineOrder, setPendingOnlineOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("efectivo");
  const [cashReceived, setCashReceived] = useState("");
  const [showCashDialog, setShowCashDialog] = useState(false);
  const [cashDialogInput, setCashDialogInput] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [showPaymentRefDialog, setShowPaymentRefDialog] = useState(false);
  const [paymentRefInput, setPaymentRefInput] = useState("");
  // Prima (down payment) on credit sales
  const [primaAmount, setPrimaAmount] = useState("");
  const [primaMethod, setPrimaMethod] = useState("efectivo"); // efectivo | sinpe | tarjeta
  const [primaReference, setPrimaReference] = useState("");
  const [primaDatafonoId, setPrimaDatafonoId] = useState("");
  const [primaCardType, setPrimaCardType] = useState("credito");
  const [customerName, setCustomerName] = useState("");
  const [customerId, setCustomerId] = useState("");  // Cédula for invoice
  const [customerPhone, setCustomerPhone] = useState("");  // Teléfono opcional (venta a crédito)
  const [customerEmail, setCustomerEmail] = useState("");
  // Credit-sale customer autocomplete (avoids duplicates like "VERO" vs "VERONICA")
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null); // {id, name, cedula, balance_colones, ...}
  const customerSearchTimerRef = useRef(null);
  const [documentType, setDocumentType] = useState("recibo");  // recibo, tiquete, factura
  const [barcodeInput, setBarcodeInput] = useState("");
  const barcodeInputRef = useRef(null);
  
  // Discount authorization (remote)
  // Cashier sets per-item discount inline in the cart. When checking out
  // with any discount > 0, we automatically open the approval flow (cajero
  // sends, MASTER approves on their device).
  const [authorizedDiscount, setAuthorizedDiscount] = useState(null); // {percentage, authorized_by, request_id}
  const [pendingDiscountRequest, setPendingDiscountRequest] = useState(null);
  const discountPollRef = useRef(null);

  // Print dialog
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printHtml, setPrintHtml] = useState("");
  const [lastSaleReceipt, setLastSaleReceipt] = useState(null);
  const [lastSaleId, setLastSaleId] = useState(null);
  
  // Invoice settings dialog
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [invoiceSettings, setInvoiceSettings] = useState({
    enabled: false,
    business_name: "",
    business_id: "",
    business_phone: "",
    business_address: "",
    atv_user: "",
    atv_password: "",
    certificate_pin: ""
  });
  
  // Reports state (defaults to today in Costa Rica time, not UTC)
  const [reportDateRange, setReportDateRange] = useState({
    start: todayCR(),
    end: todayCR()
  });
  const [salesReport, setSalesReport] = useState([]);
  const [voidedSales, setVoidedSales] = useState([]);
  const [inventoryReport, setInventoryReport] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [showVoidDialog, setShowVoidDialog] = useState(false);
  const [saleToVoid, setSaleToVoid] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [voidPassword, setVoidPassword] = useState("");
  
  // New product form
  const [productForm, setProductForm] = useState({
    code: "",
    upc: "",
    name: "",
    description: "",
    price: "",
    cost: "",
    expenses: "",  // Gastos adicionales
    margin: "30",  // Margen de ganancia por defecto 30%
    quantity: "",
    min_quantity: "",
    category: "",
    color: "",
    talla: "",
    composition: "",
    master_comments: "",
    spec_image_url: "",
    image_url: ""
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);

  // Calculate suggested price with IVA and rounding.
  // Uses adaptive rounding to avoid the old bug where a price like ₡1199
  // got rounded DOWN to ₡1000 (loss for cost ≥ ₡1000).
  //   • Precios < 2000  → redondeo a múltiplos de ₡100
  //   • Precios 2000–5000 → redondeo a múltiplos de ₡500
  //   • Precios ≥ 5000  → redondeo a múltiplos de ₡1000
  // Además, nunca permite un precio final por debajo del costo + gastos + IVA
  // (protección contra venta a pérdida).
  const calculateSuggestedPrice = (cost, expenses, margin) => {
    const totalCost = (parseFloat(cost) || 0) + (parseFloat(expenses) || 0);
    if (totalCost <= 0) return "";

    const marginMultiplier = 1 + (parseFloat(margin) || 0) / 100;
    const priceWithMargin = totalCost * marginMultiplier;
    const priceWithIVA = priceWithMargin * 1.13; // 13% IVA

    const granularity = priceWithIVA < 2000 ? 100
                       : priceWithIVA < 5000 ? 500
                       : 1000;

    const remainder = priceWithIVA % granularity;
    let roundedPrice = remainder >= granularity / 2
      ? Math.ceil(priceWithIVA / granularity) * granularity
      : Math.floor(priceWithIVA / granularity) * granularity;

    // Safety: never end up below (cost + expenses) × 1.13 → eso garantiza
    // que al menos se recupere el costo más el IVA pagado a Hacienda.
    const minPrice = totalCost * 1.13;
    if (roundedPrice < minPrice) {
      roundedPrice = Math.ceil(minPrice / granularity) * granularity;
    }

    return roundedPrice;
  };

  // Update price when cost, expenses, or margin changes
  const handleCostChange = (field, value) => {
    const newForm = { ...productForm, [field]: value };
    setProductForm(newForm);
    
    // Auto-calculate price if we have cost
    if (newForm.cost) {
      const suggestedPrice = calculateSuggestedPrice(newForm.cost, newForm.expenses, newForm.margin);
      if (suggestedPrice > 0) {
        setProductForm(prev => ({ ...prev, [field]: value, price: suggestedPrice.toString() }));
      }
    }
  };
  
  // Adjust form
  const [adjustForm, setAdjustForm] = useState({
    type: "entrada",
    quantity: "",
    reason: ""
  });
  
  const fileInputRef = useRef(null);
  const isMaster = user?.role === "master";

  // Helper to resolve product image URLs
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    // Relative paths like /api/uploads/products/... need the backend base
    const base = API.replace(/\/api$/, "");
    return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  // Fetch products
  const fetchProducts = async () => {
    if (!selectedBox) return;
    try {
      const response = await axiosInstance.get(`/inventory/products?box_id=${selectedBox.id}`);
      setProducts(response.data.products || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al cargar productos"));
    } finally {
      setLoading(false);
    }
  };

  // Fetch sales
  const fetchSales = async () => {
    if (!selectedBox) return;
    try {
      const today = todayCR();
      const response = await axiosInstance.get(`/inventory/sales?box_id=${selectedBox.id}&date=${today}`);
      setSales(response.data.sales || []);
    } catch (error) {
      console.error("Error fetching sales:", error);
    }
  };

  // Fetch datáfonos accessible in current box (para el selector cuando se paga con tarjeta)
  const fetchDatafonos = async () => {
    if (!selectedBox) return;
    try {
      const r = await axiosInstance.get(`/datafonos?box_id=${selectedBox.id}&active=true`);
      setDatafonos(r.data || []);
    } catch (error) {
      // silent - user just won't see the selector
      setDatafonos([]);
    }
  };

  // Today's inventory withdrawals (regalia/retiro/dano/otro) — summary banner.
  const [withdrawalsTodaySummary, setWithdrawalsTodaySummary] = useState({ count: 0, total_cost: 0, total_units: 0 });
  const [showWithdrawalsList, setShowWithdrawalsList] = useState(false);
  const [withdrawalsList, setWithdrawalsList] = useState([]);
  const [withdrawalsListDate, setWithdrawalsListDate] = useState("");
  const fetchWithdrawalsToday = async () => {
    if (!selectedBox) return;
    try {
      const today = todayCR();
      const r = await axiosInstance.get(`/inventory/withdrawals?box_id=${selectedBox.id}&date=${today}`);
      setWithdrawalsTodaySummary({
        count: r.data.count || 0,
        total_cost: r.data.total_cost || 0,
        total_units: r.data.total_units || 0,
      });
    } catch (error) {
      // silent
    }
  };
  const openWithdrawalsList = async (dateStr) => {
    if (!selectedBox) return;
    const d = dateStr || todayCR();
    setWithdrawalsListDate(d);
    try {
      const r = await axiosInstance.get(`/inventory/withdrawals?box_id=${selectedBox.id}&date=${d}`);
      setWithdrawalsList(r.data.withdrawals || []);
      setShowWithdrawalsList(true);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al cargar salidas"));
    }
  };

  // Fetch active cash register for accounting
  const fetchActiveCashRegister = async () => {
    if (!selectedBox) return;
    try {
      const response = await axiosInstance.get(`/cash-register/current?box_id=${selectedBox.id}`);
      if (response.data && response.data.id && response.data.status && response.data.status !== "cerrada") {
        setActiveCashRegister(response.data);
        console.log("Active cash register found:", response.data.id);
      } else {
        setActiveCashRegister(null);
        console.log("No active cash register found");
      }
    } catch (error) {
      // If no active register, that's okay
      setActiveCashRegister(null);
      console.log("Error fetching cash register:", error);
    }
  };

  useEffect(() => {
    if (!selectedBox) return;
    // Wrap fetches in IIFE so the effect itself doesn't directly setState.
    (async () => {
      await fetchProducts();
      await fetchSales();
      await fetchDatafonos();
      await fetchInvoiceSettings();
      await fetchActiveCashRegister();
      await fetchWithdrawalsToday();
      // Load a pending store order into the cart if present (from Orders.js "Enviar a caja").
      // Only triggers when the saved order matches the currently selected box.
      try {
        const raw = localStorage.getItem("palistore_pending_order");
        if (raw) {
          const pending = JSON.parse(raw);
          if (pending?.box_id === selectedBox.id && Array.isArray(pending.items)) {
            // Base cart without discount
            const baseCart = pending.items.map((it) => ({
              product_id: it.product_id,
              code: it.code,
              name: it.name,
              color: it.color || null,
              talla: it.talla || null,
              price: Number(it.price) || 0,
              quantity: Number(it.quantity) || 1,
              discount: 0,
            }));
            // Distribute the order-level discount proportionally as a per-unit
            // discount on each item. The cart engine accepts only per-unit
            // discount, so we split a global total across line subtotals.
            const subtotal = baseCart.reduce((s, c) => s + c.price * c.quantity, 0);
            const dPct = Number(pending.discount_percentage) || 0;
            const dAmt = Number(pending.discount_amount) || 0;
            let totalDiscount = 0;
            if (dAmt > 0) totalDiscount = Math.min(dAmt, subtotal);
            else if (dPct > 0) totalDiscount = Math.min(subtotal * dPct / 100, subtotal);

            const newCart = baseCart.map((c) => {
              if (totalDiscount <= 0 || subtotal <= 0 || c.quantity <= 0) return c;
              const lineSubtotal = c.price * c.quantity;
              const lineDiscount = totalDiscount * (lineSubtotal / subtotal);
              const perUnit = Math.min(c.price, lineDiscount / c.quantity);
              // Round to 2 decimals to keep math clean for the cashier
              return { ...c, discount: Math.round(perUnit * 100) / 100 };
            });
            setCart(newCart);
            if (pending.customer_name) setCustomerName(pending.customer_name);
            setPendingOnlineOrder(pending);
            toast.info(
              totalDiscount > 0
                ? `Pedido ${pending.order_number} cargado con descuento de ₡${Math.round(totalDiscount).toLocaleString()}.`
                : `Pedido ${pending.order_number} cargado. Termina la venta para confirmarlo.`
            );
          } else if (pending?.box_id && pending.box_id !== selectedBox.id) {
            toast.error(`El pedido pertenece a otra caja. Cambia a esa caja para facturarlo.`);
          }
        }
      } catch (e) { /* ignore */ }
    })();
  }, [selectedBox]);

  // Quick product lookup by code
  const handleLookupProduct = async () => {
    if (!lookupCode.trim() || !selectedBox) return;

    const needle = lookupCode.trim();
    const needleLC = needle.toLowerCase();
    setLookupLoading(true);
    try {
      // 1) Local match — codigo interno o UPC/código de barras (exacto o parcial)
      const found = products.find(p =>
        (p.code || "").toLowerCase() === needleLC ||
        (p.upc || "").toLowerCase() === needleLC ||
        (p.code || "").includes(needle) ||
        (p.upc || "").includes(needle)
      );

      if (found) {
        setLookupProduct(found);
      } else {
        // 2) Server fallback: pega directo al endpoint que busca por code Y por upc
        try {
          const r = await axiosInstance.get(`/inventory/products/code/${encodeURIComponent(needle)}?box_id=${selectedBox.id}`);
          if (r.data?.product) {
            setLookupProduct(r.data.product);
          } else {
            setLookupProduct(null);
            toast.error("Producto no encontrado");
          }
        } catch (err) {
          setLookupProduct(null);
          toast.error("Producto no encontrado");
        }
      }
    } catch (error) {
      toast.error("Error al buscar producto");
      setLookupProduct(null);
    } finally {
      setLookupLoading(false);
    }
  };

  // Handle Enter key for lookup
  const handleLookupKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLookupProduct();
    }
  };

  // Filter products by name, internal code, or UPC (scanned barcode)
  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.code?.includes(searchTerm) ||
    p.upc?.includes(searchTerm)
  );

  // Create product
  const handleCreateProduct = async () => {
    if (!productForm.code || !productForm.name || !productForm.price) {
      toast.error("Código, nombre y precio son requeridos");
      return;
    }
    
    try {
      const response = await axiosInstance.post("/inventory/products", {
        box_id: selectedBox.id,
        code: productForm.code,
        upc: productForm.upc || null,
        name: productForm.name,
        description: productForm.description || null,
        price: parseFloat(productForm.price),
        cost: parseFloat(productForm.cost) || 0,
        expenses: parseFloat(productForm.expenses) || 0,
        margin: parseFloat(productForm.margin) || 30,
        quantity: parseInt(productForm.quantity) || 0,
        min_quantity: parseInt(productForm.min_quantity) || 0,
        category: productForm.category || null,
        color: productForm.color || null,
        talla: productForm.talla || null,
        composition: productForm.composition || null,
        master_comments: productForm.master_comments || null,
        image_url: productForm.image_url || null
      });
      
      // Upload image if selected
      if (imageFile && response.data.product?.id) {
        try {
          await handleUploadImage(response.data.product.id);
        } catch (imgError) {
          toast.error("Producto creado pero la imagen no se pudo subir");
        }
      }
      
      toast.success("Producto creado");
      setShowProductDialog(false);
      setProductForm({ code: "", upc: "", name: "", description: "", price: "", cost: "", expenses: "", margin: "30", quantity: "", min_quantity: "", category: "", color: "", talla: "", composition: "", master_comments: "", spec_image_url: "", image_url: "" });
      setImageFile(null);
      setImagePreview(null);
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al crear producto"));
    }
  };

  // Upload product image
  const handleUploadImage = async (productId) => {
    if (!imageFile) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      
      const resp = await axiosInstance.post(`/inventory/products/${productId}/image`, formData);
      return resp.data;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle image file selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Imagen muy grande. Máximo 5MB");
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Upload a "spec image" (characteristics screenshot/diagram) to the product.
  // Used both from a file input and from the paste handler. Updates the form
  // immediately so the master sees the new image without reloading.
  const uploadSpecImage = async (productId, file) => {
    if (!file) return null;
    if (file.size > 8 * 1024 * 1024) { toast.error("Imagen muy grande. Máximo 8MB"); return null; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axiosInstance.post(
        `/inventory/products/${productId}/spec-image`, fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const url = r.data.spec_image_url;
      setProductForm((f) => ({ ...f, spec_image_url: url }));
      toast.success("Imagen de características guardada");
      return url;
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al subir imagen"));
      return null;
    }
  };

  const removeSpecImage = async (productId) => {
    if (!productId) { setProductForm((f) => ({ ...f, spec_image_url: "" })); return; }
    if (!window.confirm("¿Eliminar la imagen de características?")) return;
    try {
      await axiosInstance.delete(`/inventory/products/${productId}/spec-image`);
      setProductForm((f) => ({ ...f, spec_image_url: "" }));
      toast.success("Imagen eliminada");
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al eliminar"));
    }
  };

  // Returns an event handler that grabs the first image item from the
  // clipboard (Ctrl+V / Cmd+V) and runs the supplied callback with a File.
  const buildPasteImageHandler = (onFile) => (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.type && it.type.startsWith("image/")) {
        const file = it.getAsFile();
        if (file) { e.preventDefault(); onFile(file); return; }
      }
    }
  };

  // Update product
  const handleUpdateProduct = async () => {
    if (!selectedProduct) return;
    
    try {
      const updateData = {
        name: productForm.name || undefined,
        upc: productForm.upc !== undefined ? (productForm.upc || null) : undefined,
        description: productForm.description || undefined,
        price: productForm.price ? parseFloat(productForm.price) : undefined,
        cost: productForm.cost ? parseFloat(productForm.cost) : undefined,
        expenses: productForm.expenses ? parseFloat(productForm.expenses) : undefined,
        margin: productForm.margin ? parseFloat(productForm.margin) : undefined,
        quantity: productForm.quantity ? parseInt(productForm.quantity) : undefined,
        min_quantity: productForm.min_quantity ? parseInt(productForm.min_quantity) : undefined,
        category: productForm.category || undefined,
        color: productForm.color !== undefined ? (productForm.color || null) : undefined,
        talla: productForm.talla !== undefined ? (productForm.talla || null) : undefined,
        composition: productForm.composition !== undefined ? (productForm.composition || null) : undefined,
        master_comments: productForm.master_comments !== undefined ? (productForm.master_comments || null) : undefined
      };

      // If user entered a URL directly, include it
      if (productForm.image_url && !imageFile) {
        updateData.image_url = productForm.image_url;
      }

      await axiosInstance.put(`/inventory/products/${selectedProduct.id}`, updateData);
      
      // Upload image file if selected
      if (imageFile) {
        try {
          await handleUploadImage(selectedProduct.id);
        } catch (imgError) {
          toast.error("Error al subir la imagen. El producto fue actualizado sin imagen.");
          setShowEditDialog(false);
          setSelectedProduct(null);
          setImageFile(null);
          setImagePreview(null);
          fetchProducts();
          return;
        }
      }
      
      toast.success("Producto actualizado");
      setShowEditDialog(false);
      setSelectedProduct(null);
      setImageFile(null);
      setImagePreview(null);
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al actualizar producto"));
    }
  };

  // Delete product
  const handleDeleteProduct = async () => {
    if (!selectedProduct || !deletePassword) return;
    
    try {
      await axiosInstance.delete(`/inventory/products/${selectedProduct.id}?password=${deletePassword}`);
      toast.success("Producto eliminado");
      setShowDeleteDialog(false);
      setSelectedProduct(null);
      setDeletePassword("");
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al eliminar producto"));
    }
  };

  // Adjust inventory
  const handleAdjustInventory = async () => {
    if (!selectedProduct || !adjustForm.quantity || !adjustForm.reason) {
      toast.error("Cantidad y razón son requeridas");
      return;
    }
    
    try {
      await axiosInstance.post(
        `/inventory/products/${selectedProduct.id}/adjust?adjustment_type=${adjustForm.type}&quantity=${adjustForm.quantity}&reason=${encodeURIComponent(adjustForm.reason)}`
      );
      toast.success("Inventario ajustado");
      setShowAdjustDialog(false);
      setSelectedProduct(null);
      setAdjustForm({ type: "entrada", quantity: "", reason: "" });
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al ajustar inventario"));
    }
  };

  // Open the withdrawal dialog. If a product is passed (per-card button) the
  // dialog opens with that product preselected and locked. From the toolbar
  // the user picks the product inside the dialog.
  const openWithdrawalDialog = (product = null) => {
    setWithdrawalForm({
      product_id: product?.id || "",
      quantity: 1,
      reason: "regalia",
      notes: "",
    });
    setWithdrawalItems([]);
    setShowWithdrawalDialog(true);
  };

  // Add current product+quantity as an item to the multi-item cart
  const addWithdrawalItem = () => {
    const f = withdrawalForm;
    if (!f.product_id) { toast.error("Selecciona un producto"); return; }
    const qty = parseInt(f.quantity, 10);
    if (!qty || qty <= 0) { toast.error("Cantidad inválida"); return; }
    const p = products.find(x => x.id === f.product_id);
    if (!p) return;
    const existingIdx = withdrawalItems.findIndex(i => i.product_id === p.id);
    if (existingIdx >= 0) {
      const arr = [...withdrawalItems];
      arr[existingIdx] = { ...arr[existingIdx], quantity: arr[existingIdx].quantity + qty };
      setWithdrawalItems(arr);
    } else {
      setWithdrawalItems([...withdrawalItems, {
        product_id: p.id,
        code: p.code,
        name: p.name,
        cost: p.cost || 0,
        price: p.price || 0,
        quantity: qty,
        available: p.quantity,
      }]);
    }
    // Reset for next scan
    setWithdrawalForm({ ...f, product_id: "", quantity: 1, barcode_input: "" });
  };

  const removeWithdrawalItem = (productId) => {
    setWithdrawalItems(withdrawalItems.filter(i => i.product_id !== productId));
  };

  const handleWithdrawal = async () => {
    const f = withdrawalForm;
    // Build the final items list. If nothing was staged and the form has a valid
    // product, register that single item (retrocompat).
    let items = [...withdrawalItems];
    if (items.length === 0) {
      if (!f.product_id) { toast.error("Selecciona un producto o agrégalo al carrito"); return; }
      const qty = parseInt(f.quantity, 10);
      if (!qty || qty <= 0) { toast.error("Cantidad inválida"); return; }
      const p = products.find(x => x.id === f.product_id);
      if (p) items.push({ product_id: p.id, code: p.code, name: p.name, cost: p.cost || 0, price: p.price || 0, quantity: qty, available: p.quantity });
    }
    if (!f.reason) { toast.error("Selecciona un motivo"); return; }
    setWithdrawalLoading(true);
    try {
      const results = [];
      for (const it of items) {
        const res = await axiosInstance.post(`/inventory/withdrawals`, {
          box_id: selectedBox.id,
          product_id: it.product_id,
          quantity: it.quantity,
          reason: f.reason,
          notes: f.notes || "",
        });
        results.push(res.data.withdrawal);
      }
      const totalCost = results.reduce((s, w) => s + (w.total_cost || 0), 0);
      toast.success(`${results.length} salida(s) registrada(s) · Costo total ₡${totalCost.toLocaleString("es-CR")}`);
      setShowWithdrawalDialog(false);
      // Build a combined receipt to print
      if (results.length === 1) {
        setWithdrawalReceipt(results[0]);
      } else {
        setWithdrawalReceipt({
          multi: true,
          items: results,
          total_cost: totalCost,
          total_sale_price: results.reduce((s, w) => s + (w.total_sale_price || 0), 0),
          total_units: results.reduce((s, w) => s + (w.quantity || 0), 0),
          reason: f.reason,
          notes: f.notes || "",
          created_at: results[0]?.created_at || new Date().toISOString(),
          created_by: results[0]?.created_by || "",
        });
      }
      setWithdrawalItems([]);
      fetchProducts();
      fetchWithdrawalsToday();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al registrar salida"));
    } finally {
      setWithdrawalLoading(false);
    }
  };

  // Debounced search for existing customers (credit-sale autocomplete).
  // Prevents duplicate customers like "VERO" vs "VERONICA" by showing
  // matching existing customers with their pending balance.
  const searchCustomers = async (queryText) => {
    if (!selectedBox) return;
    const q = (queryText || "").trim();
    if (q.length < 2) {
      setCustomerSuggestions([]);
      setShowCustomerSuggestions(false);
      return;
    }
    try {
      const r = await axiosInstance.get(
        `/customers/search-with-balance?box_id=${selectedBox.id}&q=${encodeURIComponent(q)}&limit=8`
      );
      setCustomerSuggestions(r.data || []);
      setShowCustomerSuggestions((r.data || []).length > 0);
    } catch (err) {
      setCustomerSuggestions([]);
      setShowCustomerSuggestions(false);
    }
  };

  const scheduleCustomerSearch = (queryText) => {
    if (customerSearchTimerRef.current) clearTimeout(customerSearchTimerRef.current);
    customerSearchTimerRef.current = setTimeout(() => searchCustomers(queryText), 200);
  };

  const pickCustomerSuggestion = (c) => {
    setSelectedCustomer(c);
    setCustomerName(c.name || "");
    setCustomerId(c.cedula || "");
    setCustomerPhone(c.phone || "");
    setShowCustomerSuggestions(false);
    setCustomerSuggestions([]);
  };

  // Quick-create: when the cashier types a new name and there are no
  // matching customers, click "➕ Crear" to spin up the customer instantly
  // (auto-assigns a 4-digit code) and select it in the same step.
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const createQuickCustomer = async () => {
    const name = (customerName || "").trim();
    if (!name || name.length < 2) {
      toast.error("Escribí un nombre válido (mínimo 2 caracteres)");
      return;
    }
    if (!selectedBox?.id) {
      toast.error("Seleccioná una caja primero");
      return;
    }
    setCreatingCustomer(true);
    try {
      const body = { box_id: selectedBox.id, name, cedula: (customerId || "").trim() || "", phone: (customerPhone || "").trim() || "" };
      const r = await axiosInstance.post(`/customers`, body);
      const c = r.data || {};
      toast.success(`Cliente #${c.customer_code} creado`);
      pickCustomerSuggestion({ ...c, has_pending_debt: false, balance_colones: 0 });
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo crear el cliente"));
    } finally {
      setCreatingCustomer(false);
    }
  };

  // Lookup por código de 4 dígitos: al presionar Enter en el campo Nombre,
  // si el valor es numérico se resuelve como código (auto-pad a 4 dígitos).
  // Si es un nombre no numérico y no hay coincidencias, crea el cliente rápido.
  const handleCustomerCodeEnter = async (e) => {
    if (e.key !== "Enter") return;
    const raw = (customerName || "").trim();
    if (!raw) return;
    e.preventDefault();
    if (/^\d{1,4}$/.test(raw)) {
      try {
        const boxParam = selectedBox?.id ? `?box_id=${selectedBox.id}` : "";
        const r = await axiosInstance.get(`/customers/by-code/${raw}${boxParam}`);
        if (r.data?.id) pickCustomerSuggestion({ ...r.data, has_pending_debt: false, balance_colones: 0 });
      } catch (err) {
        toast.error(`Cliente con código ${raw.padStart(4, "0")} no encontrado`);
      }
      return;
    }
    // Non-numeric name: if no suggestions match, quick-create.
    if (paymentMethod === "credito" && !selectedCustomer && (customerSuggestions?.length || 0) === 0) {
      await createQuickCustomer();
    }
  };

  // Add to cart by barcode
  const addByCode = async (code) => {
    const response = await axiosInstance.get(`/inventory/products/code/${encodeURIComponent(code)}?box_id=${selectedBox.id}`);
    const product = response.data.product;
    setCart(prev => prev.find(c => c.product_id === product.id)
      ? prev.map(c => c.product_id === product.id ? { ...c, quantity: c.quantity + 1 } : c)
      : [...prev, { product_id: product.id, code: product.code, name: product.name, price: product.price, quantity: 1, discount: 0, available: product.quantity }]);
    return product;
  };

  const handleCameraScan = async (code) => {
    try {
      const product = await addByCode(code);
      toast.success(`${product.name} agregado`, { duration: 1500 });
      return true;
    } catch (_e) {
      toast.error(`Código ${code} no encontrado`, { duration: 2000 });
      return false;
    }
  };

  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    if (!barcodeInput.trim()) return;
    
    try {
      await addByCode(barcodeInput.trim());
      setBarcodeInput("");
      barcodeInputRef.current?.focus();
    } catch (_error) {
      toast.error("Producto no encontrado");
      setBarcodeInput("");
    }
  };

  // Add product to cart from list
  const addToCart = (product) => {
    const existing = cart.find(c => c.product_id === product.id);
    if (existing) {
      if (existing.quantity < product.quantity) {
        setCart(cart.map(c => c.product_id === product.id ? { ...c, quantity: c.quantity + 1 } : c));
      } else {
        toast.error("No hay suficiente inventario");
      }
    } else {
      if (product.quantity > 0) {
        setCart([...cart, {
          product_id: product.id,
          code: product.code,
          name: product.name,
          price: product.price,
          quantity: 1,
          discount: 0,
          available: product.quantity
        }]);
      } else {
        toast.error("Producto sin inventario");
      }
    }
  };

  // Update cart quantity
  const updateCartQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        if (newQty > item.available) {
          toast.error("No hay suficiente inventario");
          return item;
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  // Set inline per-item discount (in colones, per unit). Validated to keep
  // 0 <= discount <= price so the line never goes below 0.
  const updateCartDiscount = (productId, value) => {
    const v = Math.max(0, parseInt(value || "0", 10) || 0);
    setCart(cart.map(item => {
      if (item.product_id !== productId) return item;
      const capped = Math.min(v, item.price);
      return { ...item, discount: capped };
    }));
    // Any change to discounts invalidates a previously-authorized MASTER
    // approval (because the cart changed). Force a new request.
    if (authorizedDiscount) setAuthorizedDiscount(null);
  };

  // Remove from cart
  const removeFromCart = (productId) => {
    setCart(cart.filter(c => c.product_id !== productId));
  };

  // Calculate cart total based on per-item inline discounts.
  //   lineSubtotal = price * qty
  //   lineDiscount = discount_per_unit * qty
  //   lineTotal    = lineSubtotal - lineDiscount
  const cartSubtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const discountAmount = cart.reduce((sum, item) => sum + ((item.discount || 0) * item.quantity), 0);
  const cartTotal = cartSubtotal - discountAmount;
  const discountPercentage = cartSubtotal > 0 ? (discountAmount / cartSubtotal) * 100 : 0;

  // Request discount approval from MASTER. Called automatically before
  // checkout when the cart has any inline per-item discounts. The MASTER
  // sees the per-item breakdown (sent via products_summary) and approves
  // the whole cart at once.
  const handleRequestDiscount = async () => {
    if (discountAmount <= 0) return null;
    try {
      const productsSummary = cart
        .map(c => c.discount > 0
          ? `${c.name}(${c.quantity})[-₡${(c.discount * c.quantity).toLocaleString("es-CR")}]`
          : `${c.name}(${c.quantity})`)
        .join(", ");
      const response = await axiosInstance.post("/inventory/discount-requests", {
        box_id: selectedBox.id,
        percentage: parseFloat(discountPercentage.toFixed(4)),
        cart_total: cartSubtotal,
        products_summary: productsSummary
      });
      setPendingDiscountRequest(response.data.request);
      toast.info("Solicitud enviada. Esperando aprobación del MASTER...");
      startDiscountPolling(response.data.request.id);
      return response.data.request;
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al solicitar descuento"));
      return null;
    }
  };

  const startDiscountPolling = (requestId) => {
    if (discountPollRef.current) clearInterval(discountPollRef.current);
    discountPollRef.current = setInterval(async () => {
      try {
        const resp = await axiosInstance.get(`/inventory/discount-requests/${requestId}`);
        const req = resp.data.request;
        if (req.status === "approved") {
          clearInterval(discountPollRef.current);
          discountPollRef.current = null;
          setAuthorizedDiscount({ percentage: req.approved_percentage, authorized_by: req.approved_by, request_id: req.id });
          setPendingDiscountRequest(null);
          toast.success(`Descuento de ₡${discountAmount.toLocaleString("es-CR")} aprobado por ${req.approved_by}`);
        } else if (req.status === "rejected") {
          clearInterval(discountPollRef.current);
          discountPollRef.current = null;
          setPendingDiscountRequest(null);
          toast.error("Descuento rechazado por el MASTER");
        }
      } catch (err) {
        // silent
      }
    }, 3000);
  };

  const cancelDiscountRequest = () => {
    if (discountPollRef.current) clearInterval(discountPollRef.current);
    discountPollRef.current = null;
    setPendingDiscountRequest(null);
  };

  const clearDiscount = () => {
    // Clear all inline discounts and any prior MASTER authorization.
    setCart(cart.map(c => ({ ...c, discount: 0 })));
    setAuthorizedDiscount(null);
  };

  // Process sale
  const handleSale = async () => {
    if (cart.length === 0) {
      toast.error("El carrito está vacío");
      return;
    }
    
    // Validate factura requires customer ID
    if (documentType === "factura" && !customerId) {
      toast.error("La factura electrónica requiere cédula del cliente");
      return;
    }

    // Credit sales need at least the customer name (cédula es OPCIONAL).
    if (paymentMethod === "credito") {
      if (!customerName || customerName.trim().length < 2) {
        toast.error("La venta a crédito requiere el nombre del cliente");
        return;
      }
    }

    // Validate prima (down payment) on credit sales
    if (paymentMethod === "credito" && Number(primaAmount) > 0) {
      if (Number(primaAmount) >= Number(cartTotal)) {
        toast.error("La prima no puede ser igual o mayor al total. Usá venta al contado.");
        return;
      }
      if (primaMethod === "tarjeta" && !primaDatafonoId) {
        toast.error("Seleccioná un datáfono para la prima con tarjeta");
        return;
      }
    }
    
    // Block sale if no active register (only MASTER can override)
    if (!activeCashRegister && !isMaster) {
      toast.error("No hay caja abierta. Debe abrir caja para poder vender.");
      return;
    }

    // If there are inline discounts and no current MASTER authorization,
    // gate the sale behind a discount-approval request. Master approves
    // from the Dashboard, the polling callback flips authorizedDiscount,
    // and the cashier retries "Cobrar".
    // Boxes flagged as `acts_as_master` skip this approval (trusted box).
    const boxActsAsMaster = !!selectedBox?.acts_as_master;
    if (discountAmount > 0 && !authorizedDiscount && !isMaster && !boxActsAsMaster) {
      if (pendingDiscountRequest) {
        toast.info("Aún esperando aprobación del MASTER...");
        return;
      }
      await handleRequestDiscount();
      return;
    }
    
    // SINPE or transferencia: require a comprobante number for audit trail.
    // This is purely a record — does NOT affect cash or "monto esperado".
    const needsReference = (paymentMethod === "sinpe" || paymentMethod === "transferencia");
    if (needsReference && !paymentReference) {
      setPaymentRefInput("");
      setShowPaymentRefDialog(true);
      return;
    }

    // EFECTIVO: abrir dialog "Paga con / Vuelto" antes de facturar.
    // Si el cajero aún no confirmó el monto recibido, muestra la ventana.
    if (paymentMethod === "efectivo" && !cashReceived) {
      setCashDialogInput("");
      setShowCashDialog(true);
      return;
    }
    
    try {
      const saleData = {
        box_id: selectedBox.id,
        cash_register_id: activeCashRegister?.id || null,
        products: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          discount_per_unit: item.discount || 0
        })),
        payment_method: paymentMethod,
        payment_reference: needsReference ? paymentReference : null,
        customer_name: customerName || null,
        customer_id: customerId || null,
        customer_code: selectedCustomer?.customer_code || null,
        customer_email: customerEmail || null,
        document_type: documentType,
        discount_percentage: discountAmount > 0 ? discountPercentage : null,
        discount_amount: discountAmount > 0 ? discountAmount : null,
        discount_authorized_by: authorizedDiscount?.authorized_by
          || (isMaster ? user.username : null)
          || (selectedBox?.acts_as_master ? `CAJA-MASTER:${selectedBox.name}` : null),
        // Datáfono fields (only meaningful when payment_method === 'tarjeta')
        datafono_id: paymentMethod === "tarjeta" ? (selectedDatafonoId || null) : null,
        datafono_reference: paymentMethod === "tarjeta" ? (datafonoReference || null) : null,
        card_type: paymentMethod === "tarjeta" ? cardType : null,
        // Prima on credit sales
        prima_amount: paymentMethod === "credito" && Number(primaAmount) > 0 ? Number(primaAmount) : null,
        prima_method: paymentMethod === "credito" && Number(primaAmount) > 0 ? primaMethod : null,
        prima_reference: paymentMethod === "credito" && Number(primaAmount) > 0 && (primaMethod === "sinpe" || primaMethod === "tarjeta") ? (primaReference || null) : null,
        prima_datafono_id: paymentMethod === "credito" && Number(primaAmount) > 0 && primaMethod === "tarjeta" ? (primaDatafonoId || null) : null,
        prima_card_type: paymentMethod === "credito" && Number(primaAmount) > 0 && primaMethod === "tarjeta" ? primaCardType : null,
      };
      
      const response = await axiosInstance.post("/inventory/sales", saleData);
      
      // If this sale was triggered from a store order ("Enviar a caja"),
      // mark that order as confirmed and clear the localStorage marker.
      try {
        const pendingRaw = localStorage.getItem("palistore_pending_order");
        if (pendingRaw) {
          const pending = JSON.parse(pendingRaw);
          if (pending?.order_id && response?.data?.sale?.id) {
            await axiosInstance.post(`/orders/${pending.order_id}/mark-sold`, { sale_id: response.data.sale.id });
            localStorage.removeItem("palistore_pending_order");
            toast.success(`Pedido ${pending.order_number} confirmado`);
          }
        }
      } catch (e) { /* non-blocking */ }
      
      // Show message based on payment method and if affected cash
      let message = `Venta registrada: ₡${cartTotal.toLocaleString()}`;
      if (response.data.sale.cash_transaction_id) {
        message += " (Sumado a caja)";
      }
      // Compute change if effectivo with a cash_received > total
      if (paymentMethod === "efectivo" && cashReceived) {
        const rec = parseFloat(cashReceived) || 0;
        const change = rec - cartTotal;
        if (change > 0) {
          toast.success(`💵 Vuelto al cliente: ₡${change.toLocaleString("es-CR")}`, { duration: 8000 });
        }
      }
      toast.success(message);
      
      // Store sale ID for printing
      const saleId = response.data.sale.id;
      setLastSaleId(saleId);
      
      // Generate and show print dialog
      await handlePrintReceipt(saleId, documentType);
      
      // Reset form
      setCart([]);
      setPendingOnlineOrder(null);
      setCustomerName("");
      setCustomerId("");
      setCustomerPhone("");
      setCustomerEmail("");
      setSelectedCustomer(null);
      setCustomerSuggestions([]);
      setShowCustomerSuggestions(false);
      setPaymentReference("");
      setSelectedDatafonoId("");
      setDatafonoReference("");
      setCardType("credito");
      setPrimaAmount("");
      setPrimaMethod("efectivo");
      setPrimaReference("");
      setPrimaDatafonoId("");
      setPrimaCardType("credito");
      setCashReceived("");
      setAuthorizedDiscount(null);
      setShowSaleDialog(false);
      fetchProducts();
      fetchSales();
      fetchActiveCashRegister(); // Refresh cash register data
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al procesar venta"));
    }
  };

  // Print receipt
  const handlePrintReceipt = async (saleId, docType) => {
    try {
      const response = await axiosInstance.get(`/invoice/print-format/${saleId}?document_type=${docType}`);
      setPrintHtml(response.data.html);
      setLastSaleReceipt({ ...(response.data.receipt || {}), sale_id: saleId });
      setShowPrintDialog(true);
      // Server flags repeated prints as copies so the user knows
      // immediately if the comprobante they just printed is a duplicate.
      if (response.data.receipt?.is_copy) {
        const n = response.data.receipt.print_count || 2;
        toast.info(`Esta impresión es COPIA — Duplicado #${n}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al generar comprobante"));
    }
  };

  // Share invoice via WhatsApp (opens wa.me with pre-filled message + public link)
  const sendInvoiceWhatsApp = (saleId, opts = {}) => {
    if (!saleId) { toast.error("No hay venta para compartir"); return; }
    const r = opts.receipt || lastSaleReceipt || {};
    const business = r.business_name
      || invoiceSettings?.business_name
      || selectedBox?.name
      || "PaliStore";
    const docNumber = r.document_number || opts.docNumber || `VENTA-${saleId.slice(0, 8)}`;
    const total = r.total ?? opts.total ?? 0;
    const link = `${window.location.origin}/api/public/invoice/${saleId}`;
    const lines = [
      `Hola! Aquí está el comprobante de tu compra en *${business}* 🧾`,
      "",
      `📄 ${docNumber}`,
      `💰 Total: ₡${Number(total || 0).toLocaleString("es-CR")}`,
    ];
    if (r.total_items) lines.push(`🛒 ${r.total_items} artículo(s)`);
    if (r.date) lines.push(`📅 ${r.date}${r.time ? " " + r.time : ""}`);
    lines.push("");
    lines.push(`👉 Ver comprobante completo: ${link}`);
    lines.push("");
    lines.push("¡Gracias por tu compra!");
    const msg = encodeURIComponent(lines.join("\n"));
    // Open with no specific number → WhatsApp will show contact picker
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  // Open print in new window for thermal printer
  const openPrintWindow = () => {
    const win = printHtmlInWindow(printHtml, { width: 350, height: 600 });
    if (!win) toast.error("El navegador bloqueó la ventana. Permite ventanas emergentes.");
  };

  // Fetch invoice settings
  const fetchInvoiceSettings = async () => {
    try {
      const response = await axiosInstance.get(`/invoice/settings?box_id=${selectedBox.id}`);
      if (response.data.settings) {
        setInvoiceSettings(response.data.settings);
      }
    } catch (error) {
      console.error("Error fetching invoice settings:", error);
    }
  };

  // Save invoice settings
  const saveInvoiceSettings = async () => {
    try {
      await axiosInstance.post('/invoice/settings', {
        box_id: selectedBox.id,
        ...invoiceSettings
      });
      toast.success("Configuración guardada");
      setShowSettingsDialog(false);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al guardar configuración"));
    }
  };

  // Test Hacienda credentials
  const testHaciendaCredentials = async () => {
    try {
      // First save the settings
      await axiosInstance.post('/invoice/settings', {
        box_id: selectedBox.id,
        ...invoiceSettings
      });
      
      // Then test credentials
      const response = await axiosInstance.post(`/invoice/test-credentials?box_id=${selectedBox.id}`);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al probar credenciales"));
    }
  };

  // Delete sale (Master only)
  const handleDeleteSale = async (saleId) => {
    const password = prompt("Ingrese contraseña master para eliminar venta:");
    if (!password) return;
    
    try {
      await axiosInstance.delete(`/inventory/sales/${saleId}?password=${password}`);
      toast.success("Venta eliminada e inventario restaurado");
      fetchSales();
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al eliminar venta"));
    }
  };

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get('/inventory/products/template');
      const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.excel_base64}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = response.data.filename;
      downloadLink.click();
      toast.success("Plantilla descargada");
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al descargar plantilla"));
    }
  };

  // Import products: TWO-STEP flow.
  // 1) User picks file -> we call backend with dry_run=true to get a preview.
  // 2) User confirms the preview -> we re-upload the SAME file with dry_run=false.
  const [importImages, setImportImages] = useState([]);
  const [importPreview, setImportPreview] = useState(null);  // { preview_rows, imported, updated, errors }
  const [importPreviewLoading, setImportPreviewLoading] = useState(false);
  const [importCommitLoading, setImportCommitLoading] = useState(false);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [pendingImportImages, setPendingImportImages] = useState([]);

  const buildImportFormData = (file, dryRun) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('box_id', selectedBox.id);
    fd.append('dry_run', dryRun ? 'true' : 'false');
    pendingImportImages.forEach((img) => fd.append('images', img));
    return fd;
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPendingImportFile(file);
    setPendingImportImages(importImages);  // capture current
    setImportPreviewLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('box_id', selectedBox.id);
      fd.append('dry_run', 'true');
      importImages.forEach((img) => fd.append('images', img));
      const response = await axiosInstance.post('/inventory/products/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportPreview(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al generar vista previa"));
      setPendingImportFile(null);
    } finally {
      setImportPreviewLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImportConfirm = async () => {
    if (!pendingImportFile) return;
    setImportCommitLoading(true);
    try {
      const fd = buildImportFormData(pendingImportFile, false);
      const response = await axiosInstance.post('/inventory/products/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(response.data.message || "Importación completada");
      fetchProducts();
      setShowImportDialog(false);
      setImportImages([]);
      setImportPreview(null);
      setPendingImportFile(null);
      setPendingImportImages([]);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al importar"));
    } finally {
      setImportCommitLoading(false);
    }
  };

  const handleImportCancel = () => {
    setImportPreview(null);
    setPendingImportFile(null);
    setPendingImportImages([]);
  };

  const handleImportImagesSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setImportImages(prev => [...prev, ...files]);
    toast.success(`${files.length} imagen(es) agregada(s)`);
  };

  // Option 3: Upload ONLY images and match each filename (without extension)
  // to an existing product code. Two-step preview flow: dry_run first, then commit.
  const [bulkImagesPreview, setBulkImagesPreview] = useState(null);
  const [bulkImagesPreviewLoading, setBulkImagesPreviewLoading] = useState(false);
  const [bulkImagesCommitLoading, setBulkImagesCommitLoading] = useState(false);
  const [pendingBulkFiles, setPendingBulkFiles] = useState([]);

  const handleBulkImagesUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selectedBox) {
      if (e.target) e.target.value = "";
      return;
    }
    setPendingBulkFiles(files);
    setBulkImagesPreviewLoading(true);
    setBulkImagesResult(null);
    try {
      const form = new FormData();
      form.append("box_id", selectedBox.id);
      form.append("dry_run", "true");
      files.forEach((f) => form.append("files", f));
      const resp = await axiosInstance.post(
        "/inventory/products/bulk-images",
        form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }
      );
      setBulkImagesPreview(resp.data);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al validar imágenes"));
      setPendingBulkFiles([]);
    } finally {
      setBulkImagesPreviewLoading(false);
      if (e.target) e.target.value = "";
    }
  };

  const handleBulkImagesConfirm = async () => {
    if (!pendingBulkFiles.length) return;
    // Only upload the files marked as "matched" in the preview to save bandwidth.
    // The backend would skip them anyway, but client-side filtering is faster
    // and reflects exactly what the preview promised.
    const matchedNames = new Set(
      (bulkImagesPreview?.rows || [])
        .filter((r) => r.status === "matched")
        .map((r) => r.filename)
    );
    const filesToUpload = pendingBulkFiles.filter((f) => matchedNames.has(f.name));
    if (!filesToUpload.length) {
      toast.error("Ninguna imagen coincide con productos");
      return;
    }
    setBulkImagesCommitLoading(true);
    try {
      const form = new FormData();
      form.append("box_id", selectedBox.id);
      filesToUpload.forEach((f) => form.append("files", f));
      const resp = await axiosInstance.post(
        "/inventory/products/bulk-images",
        form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }
      );
      const data = resp.data;
      setBulkImagesResult(data);
      const skipped = pendingBulkFiles.length - filesToUpload.length;
      const parts = [`${data.matched} asignadas`];
      if (skipped > 0) parts.push(`${skipped} omitidas`);
      toast.success(parts.join(" · "));
      setBulkImagesPreview(null);
      setPendingBulkFiles([]);
      await fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al subir imágenes"));
    } finally {
      setBulkImagesCommitLoading(false);
    }
  };

  const handleBulkImagesCancel = () => {
    setBulkImagesPreview(null);
    setPendingBulkFiles([]);
  };

  // Open edit dialog
  const openEditDialog = (product) => {
    setSelectedProduct(product);
    setImageFile(null);
    setImagePreview(null);
    setProductForm({
      code: product.code,
      upc: product.upc || "",
      name: product.name,
      description: product.description || "",
      price: product.price.toString(),
      cost: (product.cost || 0).toString(),
      expenses: (product.expenses || 0).toString(),
      margin: (product.margin || 30).toString(),
      quantity: product.quantity.toString(),
      min_quantity: (product.min_quantity || 0).toString(),
      category: product.category || "",
      color: product.color || "",
      talla: product.talla || "",
      composition: product.composition || "",
      master_comments: product.master_comments || "",
      spec_image_url: product.spec_image_url || "",
      image_url: product.image_url || ""
    });
    setShowEditDialog(true);
  };

  // ============== REPORTS FUNCTIONS ==============
  
  // Fetch sales report
  const fetchSalesReport = async () => {
    if (!selectedBox) return;
    setReportLoading(true);
    try {
      const response = await axiosInstance.get(`/inventory/reports/sales?box_id=${selectedBox.id}&start_date=${reportDateRange.start}&end_date=${reportDateRange.end}`);
      setSalesReport(response.data.sales || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al cargar reporte de ventas"));
    } finally {
      setReportLoading(false);
    }
  };

  // Fetch voided sales
  const fetchVoidedSales = async () => {
    if (!selectedBox) return;
    setReportLoading(true);
    try {
      const response = await axiosInstance.get(`/inventory/reports/voided?box_id=${selectedBox.id}`);
      setVoidedSales(response.data.voided_sales || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al cargar ventas anuladas"));
    } finally {
      setReportLoading(false);
    }
  };

  // Fetch inventory report
  const fetchInventoryReport = async () => {
    if (!selectedBox) return;
    setReportLoading(true);
    try {
      const response = await axiosInstance.get(`/inventory/reports/inventory?box_id=${selectedBox.id}`);
      setInventoryReport(response.data.products || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al cargar reporte de inventario"));
    } finally {
      setReportLoading(false);
    }
  };

  // Void a sale
  const handleVoidSale = async () => {
    if (!saleToVoid || !voidPassword) {
      toast.error("Complete todos los campos");
      return;
    }
    
    // Warn if no active register for cash refund
    if (saleToVoid.payment_method === "efectivo" && !activeCashRegister) {
      toast.warning("No hay caja abierta. La anulación NO afectará el efectivo en caja.");
    }
    
    try {
      const response = await axiosInstance.post(`/inventory/sales/${saleToVoid.id}/void`, {
        password: voidPassword,
        reason: voidReason || "Sin especificar",
        cash_register_id: activeCashRegister?.id || null
      });
      
      toast.success("Venta anulada exitosamente. Inventario restaurado.");
      
      // Get and print credit note
      if (response.data.credit_note?.id) {
        await handlePrintCreditNote(response.data.credit_note.id);
      }
      
      setShowVoidDialog(false);
      setSaleToVoid(null);
      setVoidReason("");
      setVoidPassword("");
      fetchSales();
      fetchSalesReport();
      fetchVoidedSales();
      fetchProducts();
      fetchActiveCashRegister();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al anular venta"));
    }
  };

  // Print credit note
  const handlePrintCreditNote = async (creditNoteId) => {
    try {
      const response = await axiosInstance.get(`/inventory/credit-note/print/${creditNoteId}`);
      if (response.data.html) {
        const win = printHtmlInWindow(response.data.html, { width: 400, height: 600 });
        if (!win) toast.error("El navegador bloqueó la ventana. Permite ventanas emergentes.");
      }
    } catch (error) {
      console.error("Error printing credit note:", error);
      toast.error("Error al imprimir nota de crédito");
    }
  };

  // === Nota de Crédito parcial (devolución de mercadería) ==================
  const openReturnDialog = (sale) => {
    // Compute per-product already-returned qty from `partial_returns` array on the sale
    // (backend also validates, this just pre-fills the max in the UI).
    setReturnSale(sale);
    setReturnQuantities({});
    setReturnReason("");
    setShowReturnDialog(true);
  };

  const computeReturnPreviewTotal = () => {
    if (!returnSale) return 0;
    let total = 0;
    for (const p of returnSale.products || []) {
      const qty = Number(returnQuantities[p.product_id] || 0);
      if (!qty || qty <= 0) continue;
      const soldQty = Number(p.quantity || 0);
      const unit = soldQty > 0 ? (Number(p.subtotal || 0) / soldQty) : Number(p.price || 0);
      total += unit * qty;
    }
    return total;
  };

  const submitPartialReturn = async () => {
    if (!returnSale) return;
    const items = Object.entries(returnQuantities)
      .map(([product_id, quantity]) => ({ product_id, quantity: Number(quantity) }))
      .filter((it) => it.quantity > 0);
    if (items.length === 0) {
      toast.error("Ingrese al menos una unidad a devolver");
      return;
    }
    if (!returnReason.trim()) {
      toast.error("El motivo de la devolución es obligatorio");
      return;
    }
    setReturnLoading(true);
    try {
      const resp = await axiosInstance.post(
        `/inventory/sales/${returnSale.id}/partial-return`,
        { items, reason: returnReason.trim(), cash_register_id: activeCashRegister?.id || null }
      );
      toast.success(resp.data?.message || "Nota de crédito creada");
      const cnId = resp.data?.credit_note?.id;
      setShowReturnDialog(false);
      setReturnSale(null);
      setReturnQuantities({});
      setReturnReason("");
      if (cnId) await handlePrintCreditNote(cnId);
      fetchProducts();
      fetchSales();
    } catch (err) {
      toast.error(getErrorMessage(err, "Error al generar nota de crédito"));
    } finally {
      setReturnLoading(false);
    }
  };

  // Export sales report to Excel
  const exportSalesReportExcel = async () => {
    try {
      const response = await axiosInstance.get(`/inventory/reports/sales/excel?box_id=${selectedBox.id}&start_date=${reportDateRange.start}&end_date=${reportDateRange.end}`);
      const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.excel_base64}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = response.data.filename;
      downloadLink.click();
      toast.success("Reporte descargado");
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al exportar"));
    }
  };

  // Export inventory report to Excel
  const exportInventoryReportExcel = async () => {
    try {
      const response = await axiosInstance.get(`/inventory/reports/inventory/excel?box_id=${selectedBox.id}`);
      const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.excel_base64}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = response.data.filename;
      downloadLink.click();
      toast.success("Reporte de inventario descargado");
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al exportar"));
    }
  };

  // ====== Historial de Cargas Excel ======
  const loadImportBatches = async () => {
    if (!selectedBox) return;
    setBatchesLoading(true);
    try {
      const resp = await axiosInstance.get(`/inventory/import-batches?box_id=${selectedBox.id}`);
      setImportBatches(resp.data.batches || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al cargar historial"));
    } finally {
      setBatchesLoading(false);
    }
  };

  const openImportHistory = async () => {
    setShowImportHistory(true);
    await loadImportBatches();
  };

  const downloadBatchReport = async (batchId, filename) => {
    setBatchActionId(batchId);
    try {
      const resp = await axiosInstance.get(`/inventory/import-batches/${batchId}/report`);
      const link = document.createElement("a");
      link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${resp.data.excel_base64}`;
      link.download = resp.data.filename || `reporte_${filename}.xlsx`;
      link.click();
      toast.success("Reporte descargado");
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al descargar reporte"));
    } finally {
      setBatchActionId(null);
    }
  };

  const revertBatch = async (batch) => {
    if (batch.reverted) {
      toast.info("Esta carga ya fue revertida");
      return;
    }
    const confirmMsg = `¿Eliminar la carga "${batch.filename}"?\n\n` +
      `Esto BORRARÁ ${batch.total_imported} productos nuevos y RESTAURARÁ ${batch.total_updated} productos actualizados a su estado anterior.\n\n` +
      `Esta acción no se puede deshacer.`;
    if (!window.confirm(confirmMsg)) return;
    setBatchActionId(batch.id);
    try {
      const resp = await axiosInstance.delete(`/inventory/import-batches/${batch.id}`);
      toast.success(resp.data.message || "Carga revertida");
      await loadImportBatches();
      await fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al revertir carga"));
    } finally {
      setBatchActionId(null);
    }
  };

  if (!selectedBox) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Selecciona una caja para ver el inventario</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Inventario</h1>
          <p className="text-slate-600">{selectedBox.name} - {products.length} productos</p>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {isMaster && (
            <>
              <Button variant="outline" onClick={() => {
                fetchInvoiceSettings();
                setShowSettingsDialog(true);
              }}>
                <Settings className="w-4 h-4 mr-2" />
                Facturación
              </Button>
              <Button variant="outline" onClick={handleDownloadTemplate}>
                <Download className="w-4 h-4 mr-2" />
                Plantilla
              </Button>
              <Button variant="outline" onClick={() => setShowImportDialog(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Importar
              </Button>
              <Button variant="outline" onClick={openImportHistory} data-testid="open-import-history-btn">
                <ClipboardList className="w-4 h-4 mr-2" />
                Historial
              </Button>
              <Button variant="outline" onClick={() => openWithdrawalDialog(null)} data-testid="open-withdrawal-btn" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                <MinusCircle className="w-4 h-4 mr-2" />
                Salida
              </Button>
              <Button onClick={() => setShowProductDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Producto
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`flex w-full justify-start overflow-x-auto no-scrollbar md:grid ${user?.role === "master" ? "md:grid-cols-5" : "md:grid-cols-4"}`}>
          <TabsTrigger value="productos">
            <Package className="w-4 h-4 mr-2" />
            Productos
          </TabsTrigger>
          <TabsTrigger value="vender">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Vender
          </TabsTrigger>
          <TabsTrigger value="ventas">
            <Barcode className="w-4 h-4 mr-2" />
            Ventas del Día
          </TabsTrigger>
          <TabsTrigger value="reportes" onClick={() => {
            fetchSalesReport();
            fetchInventoryReport();
            fetchVoidedSales();
          }}>
            <BarChart3 className="w-4 h-4 mr-2" />
            Reportes
          </TabsTrigger>
          {user?.role === "master" && (
            <TabsTrigger value="auditoria" data-testid="inventory-audit-tab">
              <Boxes className="w-4 h-4 mr-2" />
              Inventario Físico
            </TabsTrigger>
          )}
        </TabsList>

        {/* PRODUCTOS TAB */}
        <TabsContent value="productos" className="space-y-4">
          {/* Quick Product Lookup */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
                <Search className="w-4 h-4" />
                Consulta Rápida por Código
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    placeholder="Escanear o escribir código..."
                    value={lookupCode}
                    onChange={(e) => setLookupCode(e.target.value)}
                    onKeyPress={handleLookupKeyPress}
                    className="text-lg font-mono"
                  />
                </div>
                <Button onClick={handleLookupProduct} disabled={lookupLoading || !lookupCode.trim()}>
                  {lookupLoading ? "Buscando..." : "Buscar"}
                </Button>
                {lookupProduct && (
                  <Button variant="outline" onClick={() => { setLookupProduct(null); setLookupCode(""); }}>
                    Limpiar
                  </Button>
                )}
              </div>
              
              {/* Product Result */}
              {lookupProduct && (
                <div className="mt-4 p-4 bg-white rounded-lg border flex gap-4">
                  {/* Product Image */}
                  <div 
                    className={`w-32 h-32 rounded-lg border overflow-hidden bg-slate-100 flex-shrink-0 ${lookupProduct.image_url ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                    onClick={() => lookupProduct.image_url && setEnlargedImage({ src: getImageUrl(lookupProduct.image_url), name: lookupProduct.name })}
                    data-testid="lookup-product-image"
                  >
                    {lookupProduct.image_url ? (
                      <img 
                        src={getImageUrl(lookupProduct.image_url)} 
                        alt={lookupProduct.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-slate-400 text-sm">Sin imagen</div>';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm flex-col">
                        <Package className="w-8 h-8 mb-1 opacity-50" />
                        Sin imagen
                      </div>
                    )}
                  </div>
                  
                  {/* Product Info */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-800">{lookupProduct.name}</h3>
                    <p className="text-sm text-slate-500 font-mono">Código: {lookupProduct.code}</p>
                    {lookupProduct.description && (
                      <p className="text-sm text-slate-600 mt-1">{lookupProduct.description}</p>
                    )}
                    {lookupProduct.category && (
                      <span className="inline-block text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded mt-1">{lookupProduct.category}</span>
                    )}
                    
                    <div className={`grid ${isMaster ? 'grid-cols-3' : 'grid-cols-2'} gap-4 mt-3`}>
                      <div className="text-center p-2 bg-green-50 rounded">
                        <p className="text-2xl font-bold text-green-600">₡{lookupProduct.price?.toLocaleString()}</p>
                        <p className="text-xs text-slate-500">Precio Venta</p>
                      </div>
                      <div className="text-center p-2 bg-blue-50 rounded">
                        <p className={`text-2xl font-bold ${(lookupProduct.quantity - (lookupProduct.reserved_quantity || 0)) <= (lookupProduct.min_quantity || 0) ? 'text-red-600' : 'text-blue-600'}`}>
                          {lookupProduct.quantity - (lookupProduct.reserved_quantity || 0)}
                        </p>
                        <p className="text-xs text-slate-500">Disponible</p>
                        {(lookupProduct.reserved_quantity || 0) > 0 && (
                          <p className="text-xs text-amber-600 font-semibold">{lookupProduct.reserved_quantity} apartado{lookupProduct.reserved_quantity > 1 ? 's' : ''}</p>
                        )}
                      </div>
                      {isMaster && (
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-2xl font-bold text-slate-600">₡{(lookupProduct.cost || 0).toLocaleString()}</p>
                          <p className="text-xs text-slate-500">Costo</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Buscar por nombre o código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {isMaster && withdrawalsTodaySummary.count > 0 && (
            <div
              data-testid="withdrawals-today-banner"
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm"
            >
              <div className="flex items-center gap-2 text-amber-900">
                <MinusCircle className="w-4 h-4" />
                <span><strong>Salidas hoy:</strong> {withdrawalsTodaySummary.count} movimiento(s), {withdrawalsTodaySummary.total_units} unidad(es) — costo total <strong>₡{(withdrawalsTodaySummary.total_cost || 0).toLocaleString()}</strong></span>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" className="text-amber-800 hover:bg-amber-100" onClick={() => openWithdrawalsList()} data-testid="withdrawals-view-btn">
                  Ver historial
                </Button>
                <Button size="sm" variant="ghost" className="text-amber-800 hover:bg-amber-100" onClick={() => openWithdrawalDialog(null)}>
                  <Plus className="w-4 h-4 mr-1" /> Nueva salida
                </Button>
              </div>
            </div>
          )}

          {isMaster && withdrawalsTodaySummary.count === 0 && (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="outline"
                className="text-amber-800 border-amber-300"
                onClick={() => openWithdrawalsList()}
                data-testid="withdrawals-view-btn-always"
              >
                <MinusCircle className="w-4 h-4 mr-1" /> Historial de Salidas
              </Button>
            </div>
          )}

          {loading ? (
            <p className="text-center py-8 text-slate-500">Cargando...</p>
          ) : filteredProducts.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay productos en el inventario</p>
                {isMaster && <p className="text-sm mt-2">Haz clic en &quot;Nuevo Producto&quot; para agregar</p>}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {filteredProducts.map(product => (
                <Card key={product.id} className={product.quantity <= (product.min_quantity || 0) ? "border-red-300 bg-red-50" : ""}>
                  <CardContent className="py-4">
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{product.name}</h3>
                          {product.quantity <= (product.min_quantity || 0) && (
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                        <p className="text-sm text-slate-500">Código: {product.code}</p>
                        <div className="flex gap-1 flex-wrap mt-0.5">
                          {product.category && <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">{product.category}</span>}
                          {product.color && <span className="text-xs bg-pink-100 text-pink-700 px-2 py-0.5 rounded">{product.color}</span>}
                          {product.talla && <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">T:{product.talla}</span>}
                        </div>
                      </div>
                      <div className="text-center px-4">
                        <p className="text-2xl font-bold text-blue-600">₡{product.price.toLocaleString()}</p>
                        <p className="text-sm text-slate-500">Precio</p>
                      </div>
                      <div className="text-center px-4">
                        <p className={`text-2xl font-bold ${(product.quantity - (product.reserved_quantity || 0)) <= (product.min_quantity || 0) ? 'text-red-600' : 'text-green-600'}`}>
                          {product.quantity - (product.reserved_quantity || 0)}
                        </p>
                        <p className="text-sm text-slate-500">Stock</p>
                        {(product.reserved_quantity || 0) > 0 && (
                          <p className="text-xs text-amber-600 font-semibold">{product.reserved_quantity} apt.</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {isMaster && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              title={product.show_in_store === false ? "Click para mostrar en la tienda online" : "Click para ocultar de la tienda online"}
                              data-testid={`product-store-toggle-${product.id}`}
                              className={product.show_in_store === false
                                ? "border-slate-300 text-slate-500 hover:bg-slate-100"
                                : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}
                              onClick={async () => {
                                const next = product.show_in_store === false;
                                try {
                                  await axiosInstance.patch(
                                    `/inventory/products/${product.id}/show-in-store`,
                                    { show_in_store: next }
                                  );
                                  toast.success(next ? "Visible en la tienda online" : "Oculto de la tienda online");
                                  fetchProducts();
                                } catch (e) {
                                  toast.error(getErrorMessage(e, "Error al cambiar visibilidad"));
                                }
                              }}
                            >
                              {product.show_in_store === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setSelectedProduct(product); setShowAdjustDialog(true); }}>
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              title="Salida (regalía / retiro / daño)"
                              data-testid={`product-withdrawal-btn-${product.id}`}
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              onClick={() => openWithdrawalDialog(product)}
                            >
                              <MinusCircle className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openEditDialog(product)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => { setSelectedProduct(product); setShowDeleteDialog(true); }}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* VENDER TAB */}
        <TabsContent value="vender" className="space-y-4">
          {/* Block for CAJERO without open register */}
          {!activeCashRegister && !isMaster && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="py-8 text-center">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-red-400" />
                <p className="text-lg font-bold text-red-700">Caja No Abierta</p>
                <p className="text-sm text-red-600 mt-1">Debe abrir la caja registradora para poder realizar ventas de inventario.</p>
              </CardContent>
            </Card>
          )}
          {(activeCashRegister || isMaster) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Búsqueda y productos */}
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Barcode className="w-5 h-5" />
                    Escanear Código
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleBarcodeSubmit} className="flex gap-2">
                    <Input
                      ref={barcodeInputRef}
                      placeholder="Escanea o escribe código de barras..."
                      value={barcodeInput}
                      onChange={(e) => setBarcodeInput(e.target.value)}
                      autoFocus
                      className="h-12 text-base md:h-9 md:text-sm"
                    />
                    <Button type="submit" className="h-12 px-3 md:h-9 md:px-4">Agregar</Button>
                    <CameraScanButton onScan={handleCameraScan} className="h-12 w-12 p-0 shrink-0 md:h-9 md:w-9" />
                    <PriceCheckButton boxId={selectedBox?.id} className="h-12 w-12 p-0 shrink-0 md:h-9 md:w-9" />
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Productos Disponibles</CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    placeholder="Buscar producto..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mb-3 h-12 text-base md:h-9 md:text-sm"
                  />
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {filteredProducts.filter(p => (p.quantity - (p.reserved_quantity || 0)) > 0).map(product => (
                      <div key={product.id} data-testid={`sell-product-${product.id}`} className="flex justify-between items-center gap-3 p-3 md:p-2 min-h-[56px] md:min-h-0 bg-slate-50 rounded-lg md:rounded hover:bg-slate-100 active:bg-emerald-50 cursor-pointer transition-colors" onClick={() => addToCart(product)}>
                        <div className="min-w-0">
                          <p className="font-medium">{product.name}</p>
                          <p className="text-xs text-slate-500">{product.code} | Disp: {product.quantity - (product.reserved_quantity || 0)}{(product.reserved_quantity || 0) > 0 ? ` (${product.reserved_quantity} apt.)` : ''}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <p className="font-bold text-blue-600">₡{product.price.toLocaleString()}</p>
                          <span className="md:hidden flex items-center justify-center w-9 h-9 rounded-full bg-emerald-600 text-white text-xl font-bold">+</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Carrito */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Carrito ({cart.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingOnlineOrder && (
                  <div className="mb-3 p-3 rounded-lg border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 shadow-sm" data-testid="pending-online-order-banner">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 flex items-center gap-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                          Facturando pedido en línea
                        </div>
                        <div className="font-mono font-bold text-emerald-900 text-sm mt-0.5">
                          {pendingOnlineOrder.order_number}
                        </div>
                        <div className="text-xs text-slate-700 mt-0.5 truncate">
                          {pendingOnlineOrder.customer_name}
                          {pendingOnlineOrder.customer_phone && ` · ${pendingOnlineOrder.customer_phone}`}
                        </div>
                        {pendingOnlineOrder.payment_intent && (
                          <div className="text-[11px] text-slate-600 mt-0.5">
                            Pago indicado por cliente: <strong>{pendingOnlineOrder.payment_intent}</strong>
                          </div>
                        )}
                        <div className="text-[11px] text-emerald-800 mt-1">
                          Aplica descuentos y elige el método de pago, luego pulsa <strong>Procesar venta</strong> para confirmar el pedido.
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs text-slate-600 hover:bg-emerald-100"
                        onClick={() => {
                          if (!window.confirm("¿Quitar el pedido del carrito? El pedido seguirá pendiente en /orders.")) return;
                          localStorage.removeItem("palistore_pending_order");
                          setPendingOnlineOrder(null);
                          setCart([]);
                          setCustomerName("");
                          toast.info("Pedido removido del carrito");
                        }}
                        data-testid="clear-pending-order-btn"
                      >
                        Quitar
                      </Button>
                    </div>
                  </div>
                )}
                {cart.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Carrito vacío</p>
                ) : (
                  <div className="space-y-3">
                    {cart.map(item => {
                      const lineTotal = (item.price - (item.discount || 0)) * item.quantity;
                      return (
                      <div key={item.product_id} className="p-3 bg-slate-50 rounded space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{item.name}</p>
                            <p className="text-xs text-slate-500">₡{item.price.toLocaleString()} c/u</p>
                          </div>
                          <div className="flex items-center gap-1.5 md:gap-1 shrink-0">
                            <Button size="sm" variant="outline" className="h-10 w-10 text-lg md:h-7 md:w-7 md:text-sm p-0" onClick={() => updateCartQuantity(item.product_id, -1)} data-testid={`cart-minus-${item.product_id}`}>-</Button>
                            <span className="w-7 md:w-6 text-center font-bold text-base md:text-sm">{item.quantity}</span>
                            <Button size="sm" variant="outline" className="h-10 w-10 text-lg md:h-7 md:w-7 md:text-sm p-0" onClick={() => updateCartQuantity(item.product_id, 1)} data-testid={`cart-plus-${item.product_id}`}>+</Button>
                            <Button size="sm" variant="destructive" className="h-10 w-10 md:h-7 md:w-7 p-0" onClick={() => removeFromCart(item.product_id)} data-testid={`cart-remove-${item.product_id}`}>
                              <Trash2 className="w-4 h-4 md:w-3 md:h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <div className="flex items-center gap-1 flex-1 min-w-0">
                            <Label className="text-xs text-slate-500 whitespace-nowrap">Desc. ₡</Label>
                            <Input
                              type="number"
                              min="0"
                              step="100"
                              placeholder="0"
                              value={item.discount || ""}
                              onChange={(e) => updateCartDiscount(item.product_id, e.target.value)}
                              className="h-10 w-24 md:h-7 md:w-20 text-sm"
                              data-testid={`item-discount-input-${item.product_id}`}
                            />
                            {(item.discount || 0) > 0 && (
                              <span className="text-xs text-amber-700 truncate">
                                = ₡{(item.price - item.discount).toLocaleString()} c/u
                              </span>
                            )}
                          </div>
                          <p className="font-bold text-right">₡{lineTotal.toLocaleString()}</p>
                        </div>
                      </div>
                    );})}
                    
                    <div className="border-t pt-3 mt-3">
                      {/* Discount summary: shown only when there are inline discounts */}
                      {discountAmount > 0 && (
                        <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded-lg space-y-1">
                          <div className="flex justify-between text-sm">
                            <span>Subtotal:</span>
                            <span>₡{cartSubtotal.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-sm font-bold text-amber-700">
                            <span>Descuento ({discountPercentage.toFixed(2)}%):</span>
                            <span>-₡{discountAmount.toLocaleString()}</span>
                          </div>
                          {authorizedDiscount ? (
                            <p className="text-xs text-green-700 mt-1" data-testid="discount-authorized-banner">
                              ✓ Aprobado por: {authorizedDiscount.authorized_by}
                            </p>
                          ) : pendingDiscountRequest ? (
                            <p className="text-xs text-amber-600 mt-1 animate-pulse" data-testid="discount-pending-banner">
                              ⏳ Esperando aprobación del MASTER...
                            </p>
                          ) : (isMaster || selectedBox?.acts_as_master) ? (
                            <p className="text-xs text-green-700 mt-1">
                              ✓ {isMaster ? "Modo MASTER" : "Caja con permisos MASTER"} — descuento auto-aprobado
                            </p>
                          ) : (
                            <p className="text-xs text-slate-600 mt-1">
                              Al cobrar se enviará solicitud al MASTER para aprobación.
                            </p>
                          )}
                          <button onClick={clearDiscount} className="text-xs text-red-500 hover:text-red-700 underline" data-testid="clear-discount-btn">Quitar todos los descuentos</button>
                        </div>
                      )}
                      <div className="flex justify-between text-xl font-bold">
                        <span>TOTAL:</span>
                        <span className="text-green-600">₡{cartTotal.toLocaleString()}</span>
                      </div>
                    </div>

                    <div className="space-y-3 pt-3">
                      {/* Tipo de Documento */}
                      <div>
                        <Label className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Tipo de Documento
                        </Label>
                        <Select value={documentType} onValueChange={setDocumentType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="recibo">
                              <span className="flex items-center gap-2">
                                <Receipt className="w-4 h-4" /> Recibo (sin validez fiscal)
                              </span>
                            </SelectItem>
                            <SelectItem value="tiquete">
                              <span className="flex items-center gap-2">
                                <FileText className="w-4 h-4" /> Tiquete Electrónico
                              </span>
                            </SelectItem>
                            <SelectItem value="factura">
                              <span className="flex items-center gap-2">
                                <FileText className="w-4 h-4" /> Factura Electrónica
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {documentType !== "recibo" && !invoiceSettings.enabled && (
                          <p className="text-xs text-amber-600 mt-1">
                            ⚠️ Credenciales de Hacienda no configuradas. Se generará sin validez fiscal.
                          </p>
                        )}
                      </div>

                      <div>
                        <Label>Método de Pago</Label>
                        <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="efectivo">Efectivo</SelectItem>
                            <SelectItem value="sinpe">SINPE</SelectItem>
                            <SelectItem value="transferencia">Transferencia</SelectItem>
                            <SelectItem value="tarjeta">Tarjeta</SelectItem>
                            <SelectItem value="credito">Crédito (deuda)</SelectItem>
                          </SelectContent>
                        </Select>
                        {paymentMethod === "tarjeta" && (
                          <div className="mt-2 space-y-2 p-2 rounded bg-slate-50 border border-slate-200" data-testid="datafono-block">
                            {datafonos.length === 0 ? (
                              <p className="text-xs text-amber-700">
                                ⚠️ No hay datáfonos configurados. Registra la venta sin datáfono o crea uno en <b>Bancos → Datáfonos</b>.
                              </p>
                            ) : (
                              <>
                                <div>
                                  <Label className="text-xs">Datáfono</Label>
                                  <Select value={selectedDatafonoId} onValueChange={setSelectedDatafonoId}>
                                    <SelectTrigger className="h-8" data-testid="datafono-select">
                                      <SelectValue placeholder="Selecciona datáfono..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {datafonos.map((d) => (
                                        <SelectItem key={d.id} value={d.id} data-testid={`datafono-opt-${d.id}`}>
                                          {d.name}{d.brand_model ? ` · ${d.brand_model}` : ""}{d.bank_name ? ` → ${d.bank_name}` : ""}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <Label className="text-xs">Tipo</Label>
                                    <Select value={cardType} onValueChange={setCardType}>
                                      <SelectTrigger className="h-8" data-testid="card-type-select">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="credito">Crédito</SelectItem>
                                        <SelectItem value="debito">Débito</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label className="text-xs">Nº Voucher</Label>
                                    <Input
                                      value={datafonoReference}
                                      onChange={(e) => setDatafonoReference(e.target.value)}
                                      placeholder="Referencia"
                                      className="h-8"
                                      data-testid="datafono-reference-input"
                                    />
                                  </div>
                                </div>
                                {selectedDatafonoId && (
                                  <p className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                                    ✓ El monto se acreditará automáticamente al banco vinculado.
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        {paymentMethod === "credito" && (
                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                            Esta venta NO sumará a caja. Se creará una <b>deuda</b> en el módulo Deudas a nombre del cliente.
                          </p>
                        )}
                        {paymentMethod === "efectivo" && cartTotal > 0 && (
                          <div className="mt-2 p-2 border border-blue-200 bg-blue-50 rounded text-xs text-blue-800" data-testid="cash-hint">
                            💵 Al pulsar <b>Facturar y Imprimir</b> se abrirá la calculadora de vuelto.
                            {cashReceived && (
                              <span className="ml-1">
                                (Recibido: ₡{parseFloat(cashReceived).toLocaleString("es-CR")} · Vuelto: ₡{Math.max(0, parseFloat(cashReceived) - cartTotal).toLocaleString("es-CR")})
                                <button
                                  type="button"
                                  className="ml-2 underline"
                                  onClick={() => setCashReceived("")}
                                >
                                  Cambiar
                                </button>
                              </span>
                            )}
                          </div>
                        )}
                        {(paymentMethod === "sinpe" || paymentMethod === "transferencia") && (
                          <div className="text-xs mt-1">
                            {paymentReference ? (
                              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded px-2 py-1" data-testid="payment-ref-current">
                                <span className="text-emerald-800">
                                  <b>Comprobante #:</b> {paymentReference}
                                </span>
                                <button
                                  type="button"
                                  className="text-emerald-700 underline hover:text-emerald-900"
                                  onClick={() => setPaymentReference("")}
                                  data-testid="payment-ref-clear"
                                >Cambiar</button>
                              </div>
                            ) : (
                              <p className="text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                                Al cobrar se pedirá el número de comprobante del {paymentMethod === "sinpe" ? "SINPE" : "transferencia"}.
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Cliente Info */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 relative">
                        <div className="relative">
                          <Label>Cliente {(documentType === "factura" || paymentMethod === "credito") ? <span className="text-red-600">*</span> : "(opcional)"}</Label>
                          <Input
                            value={customerName}
                            onChange={(e) => {
                              setCustomerName(e.target.value);
                              setSelectedCustomer(null);
                              if (paymentMethod === "credito") scheduleCustomerSearch(e.target.value);
                            }}
                            onFocus={() => {
                              if (paymentMethod === "credito" && customerName) scheduleCustomerSearch(customerName);
                            }}
                            onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)}
                            onKeyDown={handleCustomerCodeEnter}
                            placeholder={paymentMethod === "credito" ? "Nombre o código (ej: 1 = 0001)" : "Nombre"}
                            className={paymentMethod === "credito" && !customerName ? "border-red-400" : ""}
                            data-testid="inv-customer-name-input"
                            autoComplete="off"
                          />
                          {selectedCustomer?.customer_code && (
                            <p className="mt-1 text-xs" data-testid="inv-customer-code-badge">
                              <span className="inline-block rounded bg-blue-100 text-blue-700 px-1.5 py-0.5 font-mono">#{selectedCustomer.customer_code}</span>
                            </p>
                          )}
                        </div>
                        <div className="relative">
                          <Label>Cédula <span className="text-slate-400">(opcional)</span></Label>
                          <Input
                            value={customerId}
                            onChange={(e) => {
                              setCustomerId(e.target.value);
                              setSelectedCustomer(null);
                              if (paymentMethod === "credito") scheduleCustomerSearch(e.target.value);
                            }}
                            onFocus={() => {
                              if (paymentMethod === "credito" && customerId) scheduleCustomerSearch(customerId);
                            }}
                            onBlur={() => setTimeout(() => setShowCustomerSuggestions(false), 200)}
                            placeholder="Cédula/RUC (opcional)"
                            data-testid="inv-customer-cedula-input"
                            autoComplete="off"
                          />
                          <div className="mt-2">
                            <Label>Teléfono <span className="text-slate-400">(opcional)</span></Label>
                            <Input
                              value={customerPhone}
                              onChange={(e) => setCustomerPhone(e.target.value)}
                              placeholder="Teléfono del cliente"
                              data-testid="inv-customer-phone-input"
                              autoComplete="off"
                            />
                          </div>
                        </div>
                        {paymentMethod === "credito" && customerName && customerName.trim().length >= 2 && !selectedCustomer && (
                          <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-md border border-slate-200 bg-white shadow-lg" data-testid="inv-quick-create-block">
                            {showCustomerSuggestions && customerSuggestions.length > 0 && (
                              <div className="max-h-72 overflow-y-auto">
                                <div className="sticky top-0 border-b bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500">
                                  {customerSuggestions.length} coincidencia{customerSuggestions.length !== 1 ? "s" : ""} — clic para reutilizar
                                </div>
                                {customerSuggestions.map((c) => (
                                  <button
                                    key={c.id}
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => pickCustomerSuggestion(c)}
                                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-emerald-50 focus:bg-emerald-50 focus:outline-none"
                                    data-testid={`inv-customer-suggestion-${c.id}`}
                                  >
                                    <div className="min-w-0">
                                      <div className="truncate text-sm font-semibold text-slate-800">
                                        {c.customer_code && (
                                          <span className="inline-block rounded bg-blue-100 text-blue-700 px-1.5 py-0.5 text-[10px] font-mono mr-1.5">#{c.customer_code}</span>
                                        )}
                                        {c.name || "(sin nombre)"}
                                      </div>
                                      <div className="truncate text-xs text-slate-500">
                                        Cédula: {c.cedula || "—"}{c.total_credit_sales ? ` • ${c.total_credit_sales} venta(s) a crédito` : ""}
                                      </div>
                                    </div>
                                    <div className="shrink-0 text-right">
                                      {c.has_pending_debt ? (
                                        <span className="inline-block rounded bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800">
                                          Saldo ₡{Number(c.balance_colones || 0).toLocaleString("es-CR")}
                                        </span>
                                      ) : (
                                        <span className="inline-block rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Al día</span>
                                      )}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                            <button
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={createQuickCustomer}
                              disabled={creatingCustomer}
                              data-testid="inv-quick-create-customer"
                              className="flex w-full items-center justify-between gap-3 border-t border-slate-200 bg-emerald-50 px-3 py-2 text-left text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                            >
                              <span className="text-sm font-semibold">
                                ➕ Crear cliente nuevo &ldquo;{customerName.trim()}&rdquo;
                                {customerId?.trim() ? ` · Cédula ${customerId.trim()}` : ""}
                              </span>
                              <span className="text-[11px] font-mono text-emerald-600">{creatingCustomer ? "Creando…" : "clic o Enter+"}</span>
                            </button>
                          </div>
                        )}
                      </div>
                      {paymentMethod === "credito" && selectedCustomer && (
                        <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1" data-testid="inv-selected-customer-info">
                          ✓ Reutilizando cliente <b>{selectedCustomer.name}</b>
                          {selectedCustomer.has_pending_debt
                            ? <> — saldo actual <b>₡{Number(selectedCustomer.balance_colones || 0).toLocaleString("es-CR")}</b>. El monto de esta venta se <b>SUMARÁ</b> a la deuda existente.</>
                            : <> — sin saldo pendiente. Se creará una nueva cuenta por cobrar.</>}
                        </p>
                      )}
                      {paymentMethod === "credito" && !selectedCustomer && (
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                          💡 Si el cliente ya tiene una cuenta por cobrar abierta, el monto se <b>SUMARÁ automáticamente</b>. Si es nuevo, se creará una cuenta.
                        </p>
                      )}

                      {paymentMethod === "credito" && (
                        <div className="rounded border border-purple-200 bg-purple-50 p-2 space-y-2" data-testid="prima-section">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-semibold text-purple-800">💰 Prima (opcional)</Label>
                            <span className="text-[11px] text-purple-700">
                              Deuda: <b>₡{(Math.max(0, Number(cartTotal) - Number(primaAmount || 0))).toLocaleString("es-CR")}</b>
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              type="number" min="0" step="1" value={primaAmount}
                              onChange={(e) => setPrimaAmount(e.target.value.replace(/^0+(?=\d)/, ""))}
                              placeholder="Monto prima ₡"
                              className="h-9"
                              data-testid="prima-amount-input"
                            />
                            <select
                              value={primaMethod}
                              onChange={(e) => setPrimaMethod(e.target.value)}
                              className="h-9 border rounded px-2 text-sm bg-white"
                              disabled={!primaAmount || Number(primaAmount) <= 0}
                              data-testid="prima-method-select"
                            >
                              <option value="efectivo">Efectivo</option>
                              <option value="sinpe">SINPE</option>
                              <option value="tarjeta">Tarjeta</option>
                            </select>
                          </div>
                          {Number(primaAmount) > 0 && primaMethod === "sinpe" && (
                            <Input
                              value={primaReference}
                              onChange={(e) => setPrimaReference(e.target.value)}
                              placeholder="Nº comprobante SINPE"
                              className="h-9"
                              data-testid="prima-sinpe-ref"
                            />
                          )}
                          {Number(primaAmount) > 0 && primaMethod === "tarjeta" && (
                            <div className="grid grid-cols-2 gap-2">
                              <select
                                value={primaDatafonoId}
                                onChange={(e) => setPrimaDatafonoId(e.target.value)}
                                className="h-9 border rounded px-2 text-sm bg-white"
                                data-testid="prima-datafono-select"
                              >
                                <option value="">-- Datáfono --</option>
                                {datafonos.map(d => (
                                  <option key={d.id} value={d.id}>{d.name}</option>
                                ))}
                              </select>
                              <select
                                value={primaCardType}
                                onChange={(e) => setPrimaCardType(e.target.value)}
                                className="h-9 border rounded px-2 text-sm bg-white"
                                data-testid="prima-card-type-select"
                              >
                                <option value="credito">Crédito</option>
                                <option value="debito">Débito</option>
                              </select>
                              <Input
                                value={primaReference}
                                onChange={(e) => setPrimaReference(e.target.value)}
                                placeholder="Voucher"
                                className="h-9 col-span-2"
                                data-testid="prima-tarjeta-ref"
                              />
                            </div>
                          )}
                          {Number(primaAmount) > 0 && Number(primaAmount) >= Number(cartTotal) && (
                            <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                              ⚠ La prima no puede ser igual o mayor al total (usá venta al contado).
                            </p>
                          )}
                        </div>
                      )}
                      {documentType !== "recibo" && (
                        <div>
                          <Label>Email (opcional - para enviar copia)</Label>
                          <Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="correo@ejemplo.com" />
                        </div>
                      )}

                      <Button
                        onClick={handleSale}
                        disabled={!!pendingDiscountRequest}
                        className="w-full bg-green-600 hover:bg-green-700 text-lg py-7 md:py-6 disabled:opacity-60"
                        data-testid="checkout-btn"
                      >
                        <Printer className="w-5 h-5 mr-2" />
                        {pendingDiscountRequest
                          ? "Esperando aprobación..."
                          : (discountAmount > 0 && !authorizedDiscount && !isMaster && !selectedBox?.acts_as_master
                              ? "Solicitar Descuento al MASTER"
                              : "Facturar y Imprimir")}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          )}
        </TabsContent>

        {/* VENTAS DEL DIA TAB */}
        <TabsContent value="ventas" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Ventas de Hoy</CardTitle>
            </CardHeader>
            <CardContent>
              {sales.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No hay ventas hoy</p>
              ) : (
                <div className="space-y-3">
                  {sales.map(sale => (
                    <Card key={sale.id}>
                      <CardContent className="py-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{new Date(sale.created_at).toLocaleTimeString()}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                sale.payment_method === 'efectivo' ? 'bg-green-100 text-green-700' :
                                sale.payment_method === 'sinpe' ? 'bg-blue-100 text-blue-700' :
                                sale.payment_method === 'tarjeta' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100'
                              }`}>
                                {sale.payment_method?.toUpperCase() || 'EFECTIVO'}
                              </span>
                              {sale.status === 'voided' && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">ANULADA</span>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">{sale.products?.length} productos</p>
                            {sale.customer_name && <p className="text-sm text-slate-500">Cliente: {sale.customer_name}</p>}
                            {sale.payment_reference && (
                              <p className="text-xs text-blue-700" data-testid={`sale-ref-${sale.id}`}>
                                Comprobante #{sale.payment_reference}
                                {sale.payment_verified ? (
                                  <span className="ml-2 inline-flex items-center gap-0.5 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 font-semibold" title={`Verificado en banco · ${sale.payment_verified_match?.bank || ''}`}>
                                    ✓ Verificado
                                  </span>
                                ) : (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const r = await axiosInstance.post(`/inventory/sales/${sale.id}/verify-sinpe`);
                                        if (r.data?.verified) {
                                          toast.success(`✓ Verificado: ${r.data.match?.bank || ''}`);
                                        } else {
                                          toast.warning(`No se encontró comprobante en banco (revisó ${r.data?.sinpes_scanned || 0} SINPEs)`);
                                        }
                                        fetchSales();
                                      } catch (err) {
                                        toast.error(getErrorMessage(err, "Error al verificar"));
                                      }
                                    }}
                                    className="ml-2 text-amber-700 underline hover:text-amber-900"
                                    data-testid={`sale-verify-${sale.id}`}
                                  >Verificar en banco</button>
                                )}
                              </p>
                            )}
                            <div className="text-xs text-slate-400 mt-1">
                              {sale.products?.map(p => `${p.name} (${p.quantity})`).join(", ")}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-xl font-bold ${sale.status === 'voided' ? 'text-red-400 line-through' : 'text-green-600'}`}>₡{sale.total?.toLocaleString()}</p>
                            <div className="flex gap-1 mt-2 justify-end">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => handlePrintReceipt(sale.id, sale.document_type || 'recibo')}
                                title="Reimprimir comprobante"
                                data-testid="reprint-receipt-btn"
                              >
                                <Printer className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                onClick={() => sendInvoiceWhatsApp(sale.id, { receipt: { business_name: invoiceSettings?.business_name || selectedBox?.name, document_number: sale.document_number, total: sale.total, total_items: (sale.products || []).reduce((s, p) => s + (p.quantity || 0), 0), date: new Date(sale.created_at).toLocaleDateString("es-CR") } })}
                                title="Enviar por WhatsApp"
                                data-testid={`whatsapp-sale-${sale.id}`}
                              >
                                <MessageCircle className="w-4 h-4" />
                              </Button>
                              {sale.status === 'voided' && sale.credit_note_id && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => handlePrintCreditNote(sale.credit_note_id)}
                                  title="Reimprimir nota de crédito"
                                  data-testid="reprint-credit-note-btn"
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                              )}
                              {sale.status !== 'voided' && isMaster && (
                                <Button size="sm" variant="destructive" onClick={() => handleDeleteSale(sale.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                              {sale.status !== 'voided' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                  onClick={() => openReturnDialog(sale)}
                                  title="Nota de Crédito (devolución parcial)"
                                  data-testid={`return-sale-${sale.id}`}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between text-xl font-bold">
                      <span>Total del Día:</span>
                      <span className="text-green-600">₡{sales.reduce((sum, s) => sum + (s.total || 0), 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* REPORTES TAB */}
        <TabsContent value="reportes" className="space-y-4">
          {/* Report Type Selector */}
          <div className="flex gap-4 flex-wrap items-end">
            <div>
              <Label>Fecha Inicio</Label>
              <Input 
                type="date" 
                value={reportDateRange.start}
                onChange={(e) => setReportDateRange({...reportDateRange, start: e.target.value})}
                className="w-40"
              />
            </div>
            <div>
              <Label>Fecha Fin</Label>
              <Input 
                type="date" 
                value={reportDateRange.end}
                onChange={(e) => setReportDateRange({...reportDateRange, end: e.target.value})}
                className="w-40"
              />
            </div>
            <Button onClick={fetchSalesReport} variant="outline">
              <Search className="w-4 h-4 mr-2" />
              Buscar Ventas
            </Button>
          </div>

          {/* Reports Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sales Report */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Reporte de Ventas
                </CardTitle>
                <Button size="sm" variant="outline" onClick={exportSalesReportExcel}>
                  <Download className="w-4 h-4 mr-1" />
                  Excel
                </Button>
              </CardHeader>
              <CardContent>
                {reportLoading ? (
                  <p className="text-center py-4 text-slate-500">Cargando...</p>
                ) : salesReport.length === 0 ? (
                  <p className="text-center py-4 text-slate-500">No hay ventas en el período</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-3">
                    <div className="grid grid-cols-6 gap-2 text-xs font-semibold text-slate-600 pb-2 border-b">
                      <span>Fecha</span>
                      <span>Documento</span>
                      <span>Pago</span>
                      <span>Cliente</span>
                      <span className="text-right">Total</span>
                      <span className="text-right">Acciones</span>
                    </div>
                    {salesReport.map((sale) => (
                      <div key={sale.id} className="grid grid-cols-6 gap-2 text-sm py-1 border-b border-slate-100">
                        <span>{new Date(sale.created_at).toLocaleDateString()}</span>
                        <span className="text-xs">{sale.document_type?.toUpperCase() || 'RECIBO'}</span>
                        <span className={`text-xs px-1 py-0.5 rounded ${
                          sale.payment_method === 'efectivo' ? 'bg-green-100 text-green-700' :
                          sale.payment_method === 'sinpe' ? 'bg-blue-100 text-blue-700' :
                          sale.payment_method === 'tarjeta' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100'
                        }`}>
                          {sale.payment_method?.toUpperCase() || 'EFECTIVO'}
                        </span>
                        <span className="truncate">{sale.customer_name || '-'}</span>
                        <div className="text-right">
                          <span className={`font-medium ${sale.status === 'voided' ? 'text-red-400 line-through' : ''}`}>₡{sale.total?.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handlePrintReceipt(sale.id, sale.document_type || 'recibo')}
                            className="text-blue-500 hover:text-blue-700 p-0.5"
                            title="Reimprimir comprobante"
                            data-testid="report-reprint-receipt-btn"
                          >
                            <Printer className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => sendInvoiceWhatsApp(sale.id, { receipt: { business_name: invoiceSettings?.business_name || selectedBox?.name, document_number: sale.document_number, total: sale.total } })}
                            className="text-emerald-500 hover:text-emerald-700 p-0.5"
                            title="Enviar por WhatsApp"
                            data-testid="report-whatsapp-btn"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                          </button>
                          {sale.status === 'voided' && sale.credit_note_id && (
                            <button
                              onClick={() => handlePrintCreditNote(sale.credit_note_id)}
                              className="text-red-500 hover:text-red-700 p-0.5"
                              title="Reimprimir nota de crédito"
                              data-testid="report-reprint-cn-btn"
                            >
                              <FileText className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {sale.status !== 'voided' && isMaster && (
                            <button 
                              data-testid="void-sale-btn"
                              onClick={() => { setSaleToVoid(sale); setShowVoidDialog(true); }}
                              className="text-red-500 hover:text-red-700 p-0.5"
                              title="Anular venta"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {sale.status !== 'voided' && (
                            <button
                              onClick={() => openReturnDialog(sale)}
                              className="text-amber-600 hover:text-amber-800 p-0.5"
                              title="Nota de Crédito (devolución parcial)"
                              data-testid={`report-return-sale-${sale.id}`}
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="pt-2 flex justify-between font-bold text-green-600">
                      <span>Total Período:</span>
                      <span>₡{salesReport.reduce((sum, s) => sum + (s.total || 0), 0).toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Voided Sales Report */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-600" />
                  Ventas Anuladas
                </CardTitle>
              </CardHeader>
              <CardContent>
                {voidedSales.length === 0 ? (
                  <p className="text-center py-4 text-slate-500">No hay ventas anuladas</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-3">
                    <div className="grid grid-cols-5 gap-2 text-xs font-semibold text-slate-600 pb-2 border-b">
                      <span>Fecha</span>
                      <span>Total</span>
                      <span>Razón</span>
                      <span>Por</span>
                      <span className="text-right">Acciones</span>
                    </div>
                    {voidedSales.map((sale) => (
                      <div key={sale.id} className="grid grid-cols-5 gap-2 text-sm py-1 border-b border-slate-100 text-red-600">
                        <span>{new Date(sale.voided_at || sale.created_at).toLocaleDateString()}</span>
                        <span>₡{sale.total?.toLocaleString()}</span>
                        <span className="truncate text-xs">{sale.void_reason || '-'}</span>
                        <span className="text-xs">{sale.voided_by || '-'}</span>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handlePrintReceipt(sale.id, sale.document_type || 'recibo')}
                            className="text-blue-500 hover:text-blue-700 p-0.5"
                            title="Reimprimir comprobante original"
                            data-testid="voided-reprint-receipt-btn"
                          >
                            <Printer className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => sendInvoiceWhatsApp(sale.id, { receipt: { business_name: invoiceSettings?.business_name || selectedBox?.name, document_number: sale.document_number, total: sale.total } })}
                            className="text-emerald-500 hover:text-emerald-700 p-0.5"
                            title="Enviar por WhatsApp"
                            data-testid="voided-whatsapp-btn"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                          </button>
                          {sale.credit_note_id && (
                            <button
                              onClick={() => handlePrintCreditNote(sale.credit_note_id)}
                              className="text-red-500 hover:text-red-700 p-0.5"
                              title="Reimprimir nota de crédito"
                              data-testid="voided-reprint-cn-btn"
                            >
                              <FileText className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <div className="pt-2 flex justify-between font-bold text-red-600">
                      <span>Total Anulado:</span>
                      <span>₡{voidedSales.reduce((sum, s) => sum + (s.total || 0), 0).toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Inventory Report - Full Width */}
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-blue-600" />
                  Reporte de Inventario
                </CardTitle>
                <div className="flex items-center gap-2">
                  {isMaster && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      onClick={() => setActiveTab("auditoria")}
                      data-testid="calibrate-stock-btn"
                      title="Ajustar stock al conteo físico real"
                    >
                      <ClipboardList className="w-4 h-4 mr-1" />
                      Calibrar stock
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={exportInventoryReportExcel}>
                    <Download className="w-4 h-4 mr-1" />
                    Excel
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {inventoryReport.length === 0 ? (
                  <p className="text-center py-4 text-slate-500">No hay productos en inventario</p>
                ) : (
                  <div className="space-y-2">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <div className="bg-blue-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-600">{inventoryReport.length}</p>
                        <p className="text-xs text-blue-700">Total Productos</p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-600">{inventoryReport.reduce((sum, p) => sum + (p.quantity || 0), 0)}</p>
                        <p className="text-xs text-green-700">Unidades en Stock</p>
                      </div>
                      <div className="bg-amber-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-amber-600">{inventoryReport.filter(p => p.quantity <= (p.min_quantity || 0)).length}</p>
                        <p className="text-xs text-amber-700">Stock Bajo</p>
                      </div>
                      <div className="bg-purple-50 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-600">₡{inventoryReport.reduce((sum, p) => sum + ((p.price || 0) * (p.quantity || 0)), 0).toLocaleString()}</p>
                        <p className="text-xs text-purple-700">Valor Total</p>
                      </div>
                    </div>

                    {/* Products Table */}
                    <div className="max-h-64 overflow-y-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr>
                            <th className="text-left p-2">Código</th>
                            <th className="text-left p-2">Producto</th>
                            <th className="text-right p-2">Precio</th>
                            {isMaster && <th className="text-right p-2">Costo</th>}
                            <th className="text-right p-2">Stock</th>
                            <th className="text-right p-2">Mínimo</th>
                            <th className="text-right p-2">Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inventoryReport.map((product) => (
                            <tr key={product.id} className={`border-b ${product.quantity <= (product.min_quantity || 0) ? 'bg-red-50' : ''}`}>
                              <td className="p-2 font-mono text-xs">{product.code}</td>
                              <td className="p-2">{product.name}</td>
                              <td className="p-2 text-right">₡{product.price?.toLocaleString()}</td>
                              {isMaster && <td className="p-2 text-right text-slate-500">₡{(product.cost || 0).toLocaleString()}</td>}
                              <td className="p-2 text-right font-semibold">
                                {product.quantity <= (product.min_quantity || 0) ? (
                                  <span className="text-red-600 flex items-center justify-end gap-1">
                                    <AlertTriangle className="w-3 h-3" /> {product.quantity}
                                  </span>
                                ) : product.quantity}
                              </td>
                              <td className="p-2 text-right text-slate-500">{product.min_quantity || 0}</td>
                              <td className="p-2 text-right font-medium">₡{((product.price || 0) * (product.quantity || 0)).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* INVENTARIO FISICO TAB (MASTER ONLY) */}
        {user?.role === "master" && (
          <TabsContent value="auditoria" className="space-y-4">
            <InventoryAudit boxId={selectedBox?.id} currentUser={user} />
          </TabsContent>
        )}
      </Tabs>

      {/* CREATE PRODUCT DIALOG */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Producto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código Interno *</Label>
                <Input value={productForm.code} onChange={(e) => setProductForm({...productForm, code: e.target.value})} placeholder="Ej: JSTORE32" data-testid="product-code-input" />
              </div>
              <div>
                <Label>UPC / Código de Barras</Label>
                <Input value={productForm.upc} onChange={(e) => setProductForm({...productForm, upc: e.target.value})} placeholder="Para escanear" data-testid="product-upc-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoría</Label>
                <Input value={productForm.category} onChange={(e) => setProductForm({...productForm, category: e.target.value})} />
              </div>
              <div />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Color</Label>
                <Input
                  data-testid="product-color-input"
                  value={productForm.color}
                  placeholder="Ej: ROJO, AZUL, SURTIDO"
                  onChange={(e) => setProductForm({...productForm, color: e.target.value.toUpperCase()})}
                />
              </div>
              <div>
                <Label>Talla</Label>
                <Input
                  data-testid="product-talla-input"
                  value={productForm.talla}
                  placeholder="Ej: S, M, L, U1"
                  onChange={(e) => setProductForm({...productForm, talla: e.target.value.toUpperCase()})}
                />
              </div>
            </div>
            <div>
              <Label>Composición / Materiales</Label>
              <Input
                data-testid="product-composition-input"
                value={productForm.composition}
                placeholder="Ej: 100% algodón, poliéster reciclado…"
                onChange={(e) => setProductForm({...productForm, composition: e.target.value})}
              />
            </div>
            <div>
              <Label>Comentarios del MASTER (visible en la tienda)</Label>
              <textarea
                data-testid="product-master-comments-input"
                value={productForm.master_comments}
                placeholder="Notas adicionales que verá el cliente al pulsar Características"
                onChange={(e) => setProductForm({...productForm, master_comments: e.target.value})}
                rows={2}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} />
            </div>
            <div>
              <Label>Descripción</Label>
              <Input value={productForm.description} onChange={(e) => setProductForm({...productForm, description: e.target.value})} />
            </div>
            
            {/* Sección de Costos y Precio */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-semibold text-blue-700 mb-3">Cálculo de Precio</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Costo *</Label>
                  <Input 
                    type="number" 
                    placeholder="₡0"
                    value={productForm.cost} 
                    onChange={(e) => handleCostChange('cost', e.target.value)} 
                  />
                </div>
                <div>
                  <Label className="text-xs">Gastos Adicionales</Label>
                  <Input 
                    type="number" 
                    placeholder="₡0"
                    value={productForm.expenses} 
                    onChange={(e) => handleCostChange('expenses', e.target.value)} 
                  />
                </div>
                <div>
                  <Label className="text-xs">Margen %</Label>
                  <Input 
                    type="number" 
                    placeholder="30"
                    value={productForm.margin} 
                    onChange={(e) => handleCostChange('margin', e.target.value)} 
                  />
                </div>
              </div>
              
              {/* Price calculation display */}
              {productForm.cost && (
                <div className="mt-3 p-2 bg-white rounded border text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <span className="text-slate-600">Costo Total:</span>
                    <span className="text-right font-medium">
                      ₡{((parseFloat(productForm.cost) || 0) + (parseFloat(productForm.expenses) || 0)).toLocaleString()}
                    </span>
                    <span className="text-slate-600">+ Margen ({productForm.margin || 30}%):</span>
                    <span className="text-right font-medium">
                      ₡{(((parseFloat(productForm.cost) || 0) + (parseFloat(productForm.expenses) || 0)) * (1 + (parseFloat(productForm.margin) || 30) / 100)).toLocaleString()}
                    </span>
                    <span className="text-slate-600">+ IVA (13%):</span>
                    <span className="text-right font-medium">
                      ₡{(((parseFloat(productForm.cost) || 0) + (parseFloat(productForm.expenses) || 0)) * (1 + (parseFloat(productForm.margin) || 30) / 100) * 1.13).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Precio de Venta * (Redondeado)</Label>
                <div className="relative">
                  <Input 
                    type="number" 
                    value={productForm.price} 
                    onChange={(e) => setProductForm({...productForm, price: e.target.value})} 
                    className="pr-16"
                  />
                  {productForm.price && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-600 font-medium">
                      ₡{parseFloat(productForm.price).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <Label>Cantidad Inicial</Label>
                <Input type="number" value={productForm.quantity} onChange={(e) => setProductForm({...productForm, quantity: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Stock Mínimo</Label>
                <Input type="number" value={productForm.min_quantity} onChange={(e) => setProductForm({...productForm, min_quantity: e.target.value})} />
              </div>
              <div>
                <Label>Categoría</Label>
                <Input value={productForm.category} onChange={(e) => setProductForm({...productForm, category: e.target.value})} placeholder="Ej: BEBIDAS" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Color</Label>
                <Input
                  data-testid="edit-product-color-input"
                  value={productForm.color}
                  placeholder="Ej: ROJO, AZUL, SURTIDO"
                  onChange={(e) => setProductForm({...productForm, color: e.target.value.toUpperCase()})}
                />
              </div>
              <div>
                <Label>Talla</Label>
                <Input
                  data-testid="edit-product-talla-input"
                  value={productForm.talla}
                  placeholder="Ej: S, M, L, U1"
                  onChange={(e) => setProductForm({...productForm, talla: e.target.value.toUpperCase()})}
                />
              </div>
            </div>
            <div>
              <Label>Composición / Materiales</Label>
              <Input
                data-testid="edit-product-composition-input"
                value={productForm.composition}
                placeholder="Ej: 100% algodón, poliéster reciclado…"
                onChange={(e) => setProductForm({...productForm, composition: e.target.value})}
              />
            </div>
            <div>
              <Label>Comentarios del MASTER (visible en la tienda)</Label>
              <textarea
                data-testid="edit-product-master-comments-input"
                value={productForm.master_comments}
                placeholder="Notas adicionales que verá el cliente al pulsar Características"
                onChange={(e) => setProductForm({...productForm, master_comments: e.target.value})}
                rows={2}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <div>
              <Label>Imagen de Características (paste o subir)</Label>
              <div
                tabIndex={0}
                onPaste={buildPasteImageHandler((file) => selectedProduct && uploadSpecImage(selectedProduct.id, file))}
                className="rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-3 flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="edit-spec-image-dropzone"
              >
                {productForm.spec_image_url ? (
                  <img src={getImageUrl(productForm.spec_image_url)} alt="spec" className="w-24 h-24 object-cover rounded border" />
                ) : (
                  <div className="w-24 h-24 flex items-center justify-center text-slate-400 text-xs text-center">
                    Pega imagen<br/>(Ctrl+V) o sube
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    data-testid="edit-spec-image-input"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f && selectedProduct) uploadSpecImage(selectedProduct.id, f); }}
                    className="text-xs"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">Esta imagen se mostrará en el modal "Características" de la tienda. Máx 8MB.</p>
                  {productForm.spec_image_url && (
                    <button type="button" onClick={() => removeSpecImage(selectedProduct?.id)}
                      className="mt-1 text-xs text-red-600 hover:text-red-800 underline"
                      data-testid="edit-remove-spec-image">Eliminar imagen</button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Image Upload */}
            <div className="p-3 bg-slate-50 rounded-lg border">
              <Label className="text-sm font-semibold">Imagen del Producto</Label>
              <div className="mt-2 flex gap-4 items-start">
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p className="text-xs text-slate-400 mt-1">JPG, PNG o WebP. Máximo 5MB</p>
                  <div className="mt-2">
                    <Label className="text-xs">O URL de imagen:</Label>
                    <Input 
                      type="url" 
                      placeholder="https://ejemplo.com/imagen.jpg"
                      value={productForm.image_url} 
                      onChange={(e) => setProductForm({...productForm, image_url: e.target.value})} 
                      className="text-sm"
                    />
                  </div>
                </div>
                {(imagePreview || productForm.image_url) && (
                  <div 
                    className="w-20 h-20 rounded border overflow-hidden bg-white cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setEnlargedImage({ src: imagePreview || getImageUrl(productForm.image_url), name: productForm.name || "Producto" })}
                  >
                    <img 
                      src={imagePreview || getImageUrl(productForm.image_url)} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowProductDialog(false); setImageFile(null); setImagePreview(null); }}>Cancelar</Button>
            <Button onClick={handleCreateProduct} disabled={uploadingImage}>
              {uploadingImage ? "Subiendo..." : "Crear Producto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT PRODUCT DIALOG */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Producto</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4">
            <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código Interno</Label>
                <Input value={productForm.code} disabled className="bg-slate-100" />
              </div>
              <div>
                <Label>UPC / Código de Barras</Label>
                <Input value={productForm.upc} onChange={(e) => setProductForm({...productForm, upc: e.target.value})} placeholder="Para escanear" data-testid="product-upc-input-edit" />
              </div>
            </div>
            <div>
              <Label>Nombre</Label>
              <Input value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} />
            </div>
            
            {/* Sección de Costos y Precio */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-semibold text-blue-700 mb-3">Cálculo de Precio</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Costo</Label>
                  <Input 
                    type="number" 
                    value={productForm.cost} 
                    onChange={(e) => handleCostChange('cost', e.target.value)} 
                  />
                </div>
                <div>
                  <Label className="text-xs">Gastos</Label>
                  <Input 
                    type="number" 
                    value={productForm.expenses} 
                    onChange={(e) => handleCostChange('expenses', e.target.value)} 
                  />
                </div>
                <div>
                  <Label className="text-xs">Margen %</Label>
                  <Input 
                    type="number" 
                    value={productForm.margin} 
                    onChange={(e) => handleCostChange('margin', e.target.value)} 
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Precio de Venta</Label>
                <Input type="number" value={productForm.price} onChange={(e) => setProductForm({...productForm, price: e.target.value})} />
              </div>
              <div>
                <Label>Cantidad</Label>
                <Input type="number" value={productForm.quantity} onChange={(e) => setProductForm({...productForm, quantity: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Stock Mínimo</Label>
              <Input type="number" value={productForm.min_quantity} onChange={(e) => setProductForm({...productForm, min_quantity: e.target.value})} />
            </div>

            </div>{/* end left column */}

            <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Color</Label>
                <Input
                  data-testid="edit-product-color-input2"
                  value={productForm.color}
                  placeholder="Ej: ROJO, AZUL, SURTIDO"
                  onChange={(e) => setProductForm({...productForm, color: e.target.value.toUpperCase()})}
                />
              </div>
              <div>
                <Label>Talla</Label>
                <Input
                  data-testid="edit-product-talla-input2"
                  value={productForm.talla}
                  placeholder="Ej: S, M, L, U1"
                  onChange={(e) => setProductForm({...productForm, talla: e.target.value.toUpperCase()})}
                />
              </div>
            </div>
            <div>
              <Label>Composición / Materiales</Label>
              <Input
                data-testid="edit-product-composition-input2"
                value={productForm.composition}
                placeholder="Ej: 100% algodón, poliéster reciclado…"
                onChange={(e) => setProductForm({...productForm, composition: e.target.value})}
              />
            </div>
            <div>
              <Label>Comentarios del MASTER (visible en la tienda)</Label>
              <textarea
                data-testid="edit-product-master-comments-input2"
                value={productForm.master_comments}
                placeholder="Notas adicionales que verá el cliente al pulsar Características"
                onChange={(e) => setProductForm({...productForm, master_comments: e.target.value})}
                rows={2}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <div>
              <Label>Imagen de Características (paste o subir)</Label>
              <div
                tabIndex={0}
                onPaste={buildPasteImageHandler((file) => selectedProduct && uploadSpecImage(selectedProduct.id, file))}
                className="rounded-md border-2 border-dashed border-slate-300 bg-slate-50 p-3 flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="edit-spec-image-dropzone2"
              >
                {productForm.spec_image_url ? (
                  <img src={getImageUrl(productForm.spec_image_url)} alt="spec" className="w-24 h-24 object-cover rounded border" />
                ) : (
                  <div className="w-24 h-24 flex items-center justify-center text-slate-400 text-xs text-center">
                    Pega imagen<br/>(Ctrl+V) o sube
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    data-testid="edit-spec-image-input2"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f && selectedProduct) uploadSpecImage(selectedProduct.id, f); }}
                    className="text-xs"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">Esta imagen se mostrará en el modal "Características" de la tienda. Máx 8MB.</p>
                  {productForm.spec_image_url && (
                    <button type="button" onClick={() => removeSpecImage(selectedProduct?.id)}
                      className="mt-1 text-xs text-red-600 hover:text-red-800 underline">Eliminar imagen</button>
                  )}
                </div>
              </div>
            </div>

            {/* Image Upload for Edit */}
            <div
              className="p-3 bg-slate-50 rounded-lg border"
              tabIndex={0}
              onPaste={buildPasteImageHandler((file) => {
                if (file.size > 5 * 1024 * 1024) { toast.error("Imagen muy grande. Máximo 5MB"); return; }
                setImageFile(file);
                const reader = new FileReader();
                reader.onloadend = () => setImagePreview(reader.result);
                reader.readAsDataURL(file);
                toast.info("Imagen pegada — pulsa Guardar Cambios para confirmar");
              })}
              data-testid="edit-product-image-zone"
            >
              <Label className="text-sm font-semibold">Imagen del Producto (paste o subir)</Label>
              <div className="mt-2 space-y-2">
                <div className="flex gap-4 items-start">
                  <div className="flex-1 space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <p className="text-xs text-slate-400">JPG, PNG o WebP. Máximo 5MB</p>
                    <div>
                      <Label className="text-xs text-slate-500">O pegar URL de imagen:</Label>
                      <Input
                        placeholder="https://ejemplo.com/imagen.jpg"
                        value={productForm.image_url || ""}
                        onChange={(e) => setProductForm({...productForm, image_url: e.target.value})}
                        className="text-xs mt-1"
                      />
                    </div>
                  </div>
                  {(imagePreview || productForm.image_url || selectedProduct?.image_url) && (
                    <div 
                      className="w-20 h-20 rounded border overflow-hidden bg-white flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setEnlargedImage({ src: imagePreview || getImageUrl(productForm.image_url) || getImageUrl(selectedProduct?.image_url), name: selectedProduct?.name || "Producto" })}
                    >
                      <img 
                        src={imagePreview || getImageUrl(productForm.image_url) || getImageUrl(selectedProduct?.image_url)} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            </div>{/* end right column */}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); setImageFile(null); setImagePreview(null); }}>Cancelar</Button>
            <Button onClick={handleUpdateProduct} disabled={uploadingImage}>
              {uploadingImage ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE PRODUCT DIALOG */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Producto</DialogTitle>
            <DialogDescription>
              ¿Seguro que deseas eliminar &quot;{selectedProduct?.name}&quot;? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Contraseña Master</Label>
            <Input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeletePassword(""); }}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeleteProduct}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADJUST INVENTORY DIALOG */}
      {/* WITHDRAWAL DIALOG: registers an inventory exit (regalia/retiro/dano/otro).
          Does not affect cash register but decrements stock + records cost
          for the daily report's "Salidas de Inventario" section. */}
      {/* NOTA DE CRÉDITO PARCIAL - devolución de mercadería */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent className="max-w-2xl" data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-600" />
              Nota de Crédito — Devolución
            </DialogTitle>
          </DialogHeader>
          {returnSale && (
            <div className="space-y-3">
              <div className="rounded border bg-slate-50 px-3 py-2 text-sm">
                <div><b>Factura:</b> {returnSale.document_number || returnSale.id?.slice(0, 8)} · <b>Método:</b> {(returnSale.payment_method || "").toUpperCase()}</div>
                {returnSale.customer_name && <div><b>Cliente:</b> {returnSale.customer_name}{returnSale.customer_id ? ` (${returnSale.customer_id})` : ""}</div>}
                <div><b>Total original:</b> ₡{Number(returnSale.total || 0).toLocaleString("es-CR")}</div>
              </div>

              <div className="space-y-2 max-h-72 overflow-y-auto">
                <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-600 border-b pb-1">
                  <span className="col-span-6">Producto</span>
                  <span className="col-span-2 text-right">Vendido</span>
                  <span className="col-span-2 text-right">Ya devuelto</span>
                  <span className="col-span-2 text-right">Devolver ahora</span>
                </div>
                {(returnSale.products || []).map((p) => {
                  const soldQty = Number(p.quantity || 0);
                  const alreadyReturned = (returnSale.partial_returns || []).reduce((acc, pr) => acc, 0);
                  // Per-product already-returned is not in the sale doc directly; backend will strictly validate.
                  // We conservatively cap at soldQty here.
                  const unit = soldQty > 0 ? (Number(p.subtotal || 0) / soldQty) : Number(p.price || 0);
                  const currentQty = Number(returnQuantities[p.product_id] || 0);
                  return (
                    <div key={p.product_id} className="grid grid-cols-12 gap-2 items-center text-sm py-1 border-b border-slate-100">
                      <div className="col-span-6 min-w-0">
                        <div className="truncate font-medium">{p.name}</div>
                        <div className="text-xs text-slate-500">₡{Number(unit).toLocaleString("es-CR", { maximumFractionDigits: 2 })} c/u{p.code ? ` · ${p.code}` : ""}</div>
                      </div>
                      <span className="col-span-2 text-right">{soldQty}</span>
                      <span className="col-span-2 text-right text-slate-500">—</span>
                      <div className="col-span-2 flex items-center justify-end gap-1">
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0"
                          onClick={() => setReturnQuantities({ ...returnQuantities, [p.product_id]: Math.max(0, currentQty - 1) })}
                          data-testid={`return-qty-minus-${p.product_id}`}
                        >-</Button>
                        <Input
                          type="number"
                          min="0"
                          max={soldQty}
                          value={currentQty}
                          onChange={(e) => {
                            const v = Math.max(0, Math.min(soldQty, Number(e.target.value) || 0));
                            setReturnQuantities({ ...returnQuantities, [p.product_id]: v });
                          }}
                          className="h-7 w-14 text-center px-1"
                          data-testid={`return-qty-input-${p.product_id}`}
                        />
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0"
                          onClick={() => setReturnQuantities({ ...returnQuantities, [p.product_id]: Math.min(soldQty, currentQty + 1) })}
                          data-testid={`return-qty-plus-${p.product_id}`}
                        >+</Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div>
                <Label>Motivo de la devolución *</Label>
                <Input
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  placeholder="Ej: Talla incorrecta, defecto, cliente cambió de opinión..."
                  data-testid="return-reason-input"
                />
              </div>

              <div className="rounded bg-amber-50 border border-amber-200 px-3 py-2 text-sm">
                <div className="flex justify-between"><span>Total a devolver:</span><b>₡{computeReturnPreviewTotal().toLocaleString("es-CR", { maximumFractionDigits: 2 })}</b></div>
                {returnSale.payment_method === "credito" ? (
                  <div className="text-xs text-amber-800 mt-1">Se <b>rebajará</b> este monto de la cuenta por cobrar del cliente.</div>
                ) : (
                  <div className="text-xs text-amber-800 mt-1">Se registrará una <b>salida de caja</b> por este monto (reembolso al cliente).</div>
                )}
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowReturnDialog(false)} data-testid="return-cancel-btn">Cancelar</Button>
                <Button
                  onClick={submitPartialReturn}
                  disabled={returnLoading || computeReturnPreviewTotal() <= 0}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="return-submit-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  {returnLoading ? "Procesando..." : "Generar Nota de Crédito"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showWithdrawalDialog} onOpenChange={setShowWithdrawalDialog}>
        <DialogContent data-testid="withdrawal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-600" />
              Registrar Salida de Inventario
            </DialogTitle>
            <DialogDescription>
              Para regalías, retiro del propietario o daño. Baja el stock y el costo, NO afecta la caja.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Buscar por código o código de barras</Label>
              <div className="flex gap-1">
                <Input
                  autoFocus
                  placeholder="Escanear o escribir código / barcode..."
                  value={withdrawalForm.barcode_input || ""}
                  onChange={(e) => setWithdrawalForm({ ...withdrawalForm, barcode_input: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const q = (withdrawalForm.barcode_input || "").trim().toLowerCase();
                      if (!q) return;
                      const found = products.find(p =>
                        (p.code || "").toLowerCase() === q ||
                        (p.upc || "").toLowerCase() === q ||
                        (p.barcode || "").toLowerCase() === q ||
                        (p.code || "").toLowerCase().includes(q) ||
                        (p.upc || "").toLowerCase().includes(q)
                      );
                      if (found) {
                        setWithdrawalForm({ ...withdrawalForm, product_id: found.id, barcode_input: "" });
                        toast.success(`✓ ${found.name} (stock: ${found.quantity})`);
                      } else {
                        toast.error("Producto no encontrado");
                      }
                    }
                  }}
                  data-testid="withdrawal-barcode-input"
                />
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">Escanea o escribe y pulsa Enter · o abre el desplegable abajo</p>
            </div>
            <div>
              <Label>Producto</Label>
              <Select
                value={withdrawalForm.product_id}
                onValueChange={(v) => setWithdrawalForm({ ...withdrawalForm, product_id: v })}
              >
                <SelectTrigger data-testid="withdrawal-product-select">
                  <SelectValue placeholder="Selecciona un producto..." />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.code} — {p.name} (stock: {p.quantity})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {withdrawalForm.product_id && (() => {
                const p = products.find(x => x.id === withdrawalForm.product_id);
                if (!p) return null;
                return (
                  <div className="text-[11px] mt-1 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 text-emerald-900">
                    ✓ <b>{p.name}</b> · Stock: <b>{p.quantity}</b> · Costo: ₡{(p.cost||0).toLocaleString()} · Precio: ₡{(p.price||0).toLocaleString()}
                  </div>
                );
              })()}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Cantidad</Label>
                <Input
                  type="number"
                  min={1}
                  data-testid="withdrawal-quantity-input"
                  value={withdrawalForm.quantity}
                  onChange={(e) => setWithdrawalForm({ ...withdrawalForm, quantity: e.target.value })}
                />
              </div>
              <div>
                <Label>Motivo</Label>
                <Select
                  value={withdrawalForm.reason}
                  onValueChange={(v) => setWithdrawalForm({ ...withdrawalForm, reason: v })}
                >
                  <SelectTrigger data-testid="withdrawal-reason-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regalia">🎁 Regalía</SelectItem>
                    <SelectItem value="retiro_propietario">🏠 Retiro del propietario</SelectItem>
                    <SelectItem value="dano">⚠️ Daño</SelectItem>
                    <SelectItem value="otro">📝 Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={addWithdrawalItem}
              className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="withdrawal-add-item"
              disabled={!withdrawalForm.product_id}
            >
              <Plus className="w-4 h-4 mr-1" /> Agregar otro artículo al comprobante
            </Button>

            {withdrawalItems.length > 0 && (
              <div className="border rounded max-h-56 overflow-y-auto" data-testid="withdrawal-items-list">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="p-1.5 text-left">Código</th>
                      <th className="p-1.5 text-left">Producto</th>
                      <th className="p-1.5 text-right">Cant.</th>
                      <th className="p-1.5 text-right">Costo</th>
                      <th className="p-1.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawalItems.map((it) => (
                      <tr key={it.product_id} className="border-t">
                        <td className="p-1.5 font-mono">{it.code}</td>
                        <td className="p-1.5">{it.name}</td>
                        <td className="p-1.5 text-right">{it.quantity}</td>
                        <td className="p-1.5 text-right">₡{((it.cost || 0) * it.quantity).toLocaleString("es-CR")}</td>
                        <td className="p-1.5 text-right">
                          <button
                            type="button"
                            onClick={() => removeWithdrawalItem(it.product_id)}
                            className="text-red-600 hover:text-red-800"
                            data-testid={`withdrawal-remove-${it.product_id}`}
                            title="Quitar"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-amber-50 font-bold">
                    <tr>
                      <td colSpan={2} className="p-1.5">TOTAL ({withdrawalItems.length} artículo{withdrawalItems.length > 1 ? "s" : ""})</td>
                      <td className="p-1.5 text-right">{withdrawalItems.reduce((s, i) => s + i.quantity, 0)}</td>
                      <td className="p-1.5 text-right">₡{withdrawalItems.reduce((s, i) => s + (i.cost || 0) * i.quantity, 0).toLocaleString("es-CR")}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <div>
              <Label>Notas (opcional)</Label>
              <Input
                data-testid="withdrawal-notes-input"
                value={withdrawalForm.notes}
                placeholder="Ej: cumpleaños cliente VIP, producto fallado..."
                onChange={(e) => setWithdrawalForm({ ...withdrawalForm, notes: e.target.value })}
              />
            </div>
            {withdrawalItems.length === 0 && withdrawalForm.product_id && (() => {
              const p = products.find((pp) => pp.id === withdrawalForm.product_id);
              const qty = parseInt(withdrawalForm.quantity || 0, 10) || 0;
              if (!p) return null;
              const cost = (p.cost || 0) * qty;
              const newStock = (p.quantity || 0) - qty;
              return (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                  <div className="font-semibold text-amber-900">Resumen (1 artículo):</div>
                  <div>Stock: <strong>{p.quantity}</strong> → <strong>{newStock}</strong></div>
                  <div>Costo total: <strong>₡{cost.toLocaleString()}</strong></div>
                  {newStock < 0 && <div className="text-red-600 font-bold mt-1">⚠️ Stock insuficiente</div>}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWithdrawalDialog(false)} data-testid="withdrawal-cancel-btn">
              Cancelar
            </Button>
            <Button
              onClick={handleWithdrawal}
              disabled={withdrawalLoading || (withdrawalItems.length === 0 && !withdrawalForm.product_id)}
              data-testid="withdrawal-submit-btn"
              className="bg-amber-600 hover:bg-amber-700"
            >
              {withdrawalLoading ? "Registrando..." : `Registrar Salida${withdrawalItems.length > 1 ? ` (${withdrawalItems.length})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reporte imprimible tras registrar salida */}
      <Dialog open={!!withdrawalReceipt} onOpenChange={(o) => !o && setWithdrawalReceipt(null)}>
        <DialogContent className="max-w-md" data-testid="withdrawal-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-amber-600" /> Salida de Inventario Registrada
            </DialogTitle>
            <DialogDescription>Guardado en el sistema. Podés imprimir el comprobante.</DialogDescription>
          </DialogHeader>
          {withdrawalReceipt && (() => {
            const w = withdrawalReceipt;
            const reasonLbl = { regalia: "🎁 Regalía", retiro_propietario: "🏠 Retiro del propietario", dano: "⚠️ Daño", otro: "📝 Otro" }[w.reason] || w.reason;
            if (w.multi) {
              // Multi-item receipt
              return (
                <div id="withdrawal-receipt-printable" className="font-mono text-xs bg-white p-3 border rounded space-y-1">
                  <div className="text-center font-bold mb-2">— SALIDA DE INVENTARIO —</div>
                  <div>Fecha: {new Date(w.created_at).toLocaleString("es-CR")}</div>
                  <div>Registrado por: <b>{w.created_by}</b></div>
                  <div>Motivo: <b>{reasonLbl}</b></div>
                  <div className="border-t my-2" />
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left">Código</th>
                        <th className="text-left">Producto</th>
                        <th className="text-right">Cant.</th>
                        <th className="text-right">Costo Un.</th>
                        <th className="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {w.items.map((it, idx) => (
                        <tr key={idx} className="border-b border-dashed">
                          <td>{it.product_code}</td>
                          <td>{it.product_name}</td>
                          <td className="text-right">{it.quantity}</td>
                          <td className="text-right">₡{(it.unit_cost||0).toLocaleString("es-CR")}</td>
                          <td className="text-right">₡{(it.total_cost||0).toLocaleString("es-CR")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="border-t my-2" />
                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                    <span>Artículos:</span><span className="text-right"><b>{w.items.length}</b></span>
                    <span>Unidades totales:</span><span className="text-right"><b>{w.total_units}</b></span>
                    <span className="font-bold">Costo total:</span><span className="text-right font-bold">₡{(w.total_cost||0).toLocaleString("es-CR")}</span>
                    <span>Valor venta total:</span><span className="text-right">₡{(w.total_sale_price||0).toLocaleString("es-CR")}</span>
                  </div>
                  {w.notes && <div className="mt-2 text-slate-600">Nota: {w.notes}</div>}
                  <div className="text-center mt-3 text-[10px] text-slate-500">Este documento certifica la salida de inventario. NO afecta caja.</div>
                </div>
              );
            }
            return (
              <div id="withdrawal-receipt-printable" className="font-mono text-xs bg-white p-3 border rounded space-y-1">
                <div className="text-center font-bold mb-2">— SALIDA DE INVENTARIO —</div>
                <div>Fecha: {new Date(w.created_at).toLocaleString("es-CR")}</div>
                <div>Registrado por: <b>{w.created_by}</b></div>
                <div>Motivo: <b>{reasonLbl}</b></div>
                <div className="border-t my-2" />
                <div>Código: <b>{w.product_code}</b></div>
                <div>Producto: <b>{w.product_name}</b></div>
                {w.product_description && <div>Descripción: {w.product_description}</div>}
                <div className="border-t my-2" />
                <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                  <span>Cantidad:</span><span className="text-right"><b>{w.quantity}</b></span>
                  <span>Costo unitario:</span><span className="text-right">₡{(w.unit_cost||0).toLocaleString("es-CR")}</span>
                  <span>Precio venta unit.:</span><span className="text-right">₡{(w.unit_sale_price||0).toLocaleString("es-CR")}</span>
                  <span className="font-bold">Costo total:</span><span className="text-right font-bold">₡{(w.total_cost||0).toLocaleString("es-CR")}</span>
                  <span>Valor venta total:</span><span className="text-right">₡{(w.total_sale_price||0).toLocaleString("es-CR")}</span>
                </div>
                <div className="border-t my-2" />
                <div>Stock antes: <b>{w.previous_quantity}</b></div>
                <div>Stock después: <b>{w.new_quantity}</b></div>
                {w.notes && <div className="mt-2 text-slate-600">Nota: {w.notes}</div>}
                <div className="text-center mt-3 text-[10px] text-slate-500">Este documento certifica la salida de inventario. NO afecta caja.</div>
              </div>
            );
          })()}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setWithdrawalReceipt(null)} data-testid="withdrawal-receipt-close">Cerrar</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="withdrawal-receipt-print"
              onClick={() => {
                const node = document.getElementById("withdrawal-receipt-printable");
                if (!node) return;
                const html = `<html><head><title>Salida de Inventario</title><style>body{font-family:monospace;padding:20px;font-size:12px}</style></head><body>${node.outerHTML}</body></html>`;
                printHtmlInWindow(html, { width: 400, height: 600 });
              }}
            >
              <Printer className="w-4 h-4 mr-1" /> Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historial de salidas — lista y reimpresión */}
      <Dialog open={showWithdrawalsList} onOpenChange={setShowWithdrawalsList}>
        <DialogContent className="max-w-3xl" data-testid="withdrawals-list-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MinusCircle className="w-5 h-5 text-amber-600" /> Historial de Salidas — {selectedBox?.name}
            </DialogTitle>
            <DialogDescription>Consulta e imprime salidas registradas.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs">Fecha:</Label>
              <Input
                type="date"
                value={withdrawalsListDate}
                onChange={(e) => openWithdrawalsList(e.target.value)}
                className="w-40 h-8 text-sm"
                data-testid="withdrawals-list-date"
              />
              <span className="text-xs text-slate-500">{withdrawalsList.length} salida(s)</span>
            </div>
            <div className="max-h-[500px] overflow-y-auto border rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="p-2 text-left">Hora</th>
                    <th className="p-2 text-left">Código</th>
                    <th className="p-2 text-left">Producto</th>
                    <th className="p-2 text-right">Cant.</th>
                    <th className="p-2 text-right">Costo Total</th>
                    <th className="p-2 text-right">Venta Total</th>
                    <th className="p-2 text-left">Motivo</th>
                    <th className="p-2 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawalsList.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-4 text-slate-500">No hay salidas en esta fecha</td></tr>
                  ) : withdrawalsList.map((w) => {
                    const reasonLbl = { regalia: "🎁 Regalía", retiro_propietario: "🏠 Retiro", dano: "⚠️ Daño", otro: "📝 Otro" }[w.reason] || w.reason;
                    return (
                      <tr key={w.id} className="border-t hover:bg-slate-50">
                        <td className="p-2">{new Date(w.created_at).toLocaleTimeString("es-CR", { hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="p-2 font-mono">{w.product_code}</td>
                        <td className="p-2">{w.product_name}</td>
                        <td className="p-2 text-right">{w.quantity}</td>
                        <td className="p-2 text-right font-mono">₡{(w.total_cost||0).toLocaleString("es-CR")}</td>
                        <td className="p-2 text-right font-mono">₡{(w.total_sale_price||0).toLocaleString("es-CR")}</td>
                        <td className="p-2 text-xs">{reasonLbl}</td>
                        <td className="p-2 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2"
                            onClick={() => { setWithdrawalReceipt(w); setShowWithdrawalsList(false); }}
                            data-testid={`withdrawal-reprint-${w.id}`}
                            title="Ver e imprimir"
                          >
                            <Printer className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {withdrawalsList.length > 0 && (
                  <tfoot className="bg-amber-50 font-bold">
                    <tr>
                      <td colSpan={3} className="p-2">TOTALES</td>
                      <td className="p-2 text-right">{withdrawalsList.reduce((s,w)=>s+(w.quantity||0),0)}</td>
                      <td className="p-2 text-right font-mono">₡{withdrawalsList.reduce((s,w)=>s+(w.total_cost||0),0).toLocaleString("es-CR")}</td>
                      <td className="p-2 text-right font-mono">₡{withdrawalsList.reduce((s,w)=>s+(w.total_sale_price||0),0).toLocaleString("es-CR")}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWithdrawalsList(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajustar Inventario</DialogTitle>
            <DialogDescription>
              {selectedProduct?.name} - Stock actual: {selectedProduct?.quantity}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo de Ajuste</Label>
              <Select value={adjustForm.type} onValueChange={(v) => setAdjustForm({...adjustForm, type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entrada">
                    <span className="flex items-center gap-2"><ArrowUp className="w-4 h-4 text-green-600" /> Entrada</span>
                  </SelectItem>
                  <SelectItem value="salida">
                    <span className="flex items-center gap-2"><ArrowDown className="w-4 h-4 text-red-600" /> Salida</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cantidad</Label>
              <Input type="number" value={adjustForm.quantity} onChange={(e) => setAdjustForm({...adjustForm, quantity: e.target.value})} />
            </div>
            <div>
              <Label>Razón</Label>
              <Input value={adjustForm.reason} onChange={(e) => setAdjustForm({...adjustForm, reason: e.target.value})} placeholder="Ej: Compra de mercadería, Producto dañado..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustDialog(false)}>Cancelar</Button>
            <Button onClick={handleAdjustInventory}>Ajustar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IMPORT DIALOG */}
      <Dialog open={showImportDialog} onOpenChange={(open) => { setShowImportDialog(open); if(!open) setImportImages([]); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importar Productos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Columnas del Excel:</strong><br/>
                CODIGO*, NOMBRE*, COSTO, GASTOS, MARGEN%, PRECIO_FINAL, CANTIDAD, CATEGORIA, IMAGEN_URL
              </p>
            </div>
            
            <Button variant="outline" onClick={handleDownloadTemplate} className="w-full">
              <Download className="w-4 h-4 mr-2" />
              Descargar Plantilla
            </Button>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-2">Opciones de importación:</p>
              
              {/* Option 1: ZIP file */}
              <div className="p-3 bg-green-50 rounded-lg border border-green-200 mb-3">
                <p className="text-sm font-semibold text-green-700 mb-1">Opción 1: Archivo ZIP</p>
                <p className="text-xs text-green-600 mb-2">Crea un ZIP con el Excel + imágenes (usa el nombre del archivo en IMAGEN_URL)</p>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleImport} 
                  accept=".xlsx,.xls,.zip" 
                  className="hidden" 
                />
                <Button size="sm" onClick={() => fileInputRef.current?.click()} className="w-full">
                  <Upload className="w-4 h-4 mr-2" />
                  Subir Excel o ZIP
                </Button>
              </div>
              
              {/* Option 2: Excel + separate images */}
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 mb-3">
                <p className="text-sm font-semibold text-purple-700 mb-1">Opción 2: Excel + Imágenes separadas</p>
                <p className="text-xs text-purple-600 mb-2">Primero selecciona las imágenes, luego el Excel</p>
                
                <div className="space-y-2">
                  <div>
                    <input 
                      type="file" 
                      multiple 
                      accept="image/*" 
                      onChange={handleImportImagesSelect}
                      className="w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-purple-100 file:text-purple-700"
                    />
                    {importImages.length > 0 && (
                      <p className="text-xs text-purple-600 mt-1">
                        {importImages.length} imagen(es) seleccionada(s): {importImages.map(f => f.name).join(', ')}
                      </p>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full">
                    <Upload className="w-4 h-4 mr-2" />
                    Subir Excel
                  </Button>
                </div>
              </div>

              {/* Option 3: bulk image upload to existing products (filename = code) */}
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-semibold text-amber-700 mb-1">Opción 3: Solo Imágenes (a productos existentes)</p>
                <p className="text-xs text-amber-600 mb-2">
                  Sube imágenes nombradas con el código del producto (ej. <b>JSTORE32.jpg</b>). El sistema las asignará automáticamente al producto correspondiente. No crea productos nuevos.
                </p>
                <input
                  type="file"
                  ref={bulkImagesInputRef}
                  multiple
                  accept="image/*"
                  onChange={handleBulkImagesUpload}
                  className="hidden"
                  data-testid="bulk-images-input"
                />
                <Button
                  size="sm"
                  onClick={() => bulkImagesInputRef.current?.click()}
                  disabled={bulkImagesPreviewLoading}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="bulk-images-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {bulkImagesPreviewLoading ? "Validando..." : "Subir Imágenes"}
                </Button>
                {bulkImagesResult && (
                  <div className="mt-3 text-xs space-y-1 bg-white p-2 rounded border border-amber-200 max-h-48 overflow-y-auto" data-testid="bulk-images-result">
                    <div className="flex gap-3 flex-wrap font-medium">
                      <span className="text-green-700">✓ {bulkImagesResult.matched} asignadas</span>
                      {bulkImagesResult.not_found > 0 && <span className="text-red-700">✗ {bulkImagesResult.not_found} sin producto</span>}
                      {bulkImagesResult.too_big > 0 && <span className="text-red-700">⚠ {bulkImagesResult.too_big} muy grandes</span>}
                      {bulkImagesResult.invalid > 0 && <span className="text-red-700">⚠ {bulkImagesResult.invalid} inválidas</span>}
                    </div>
                    <ul className="space-y-0.5 mt-1">
                      {bulkImagesResult.rows.slice(0, 30).map((r, i) => (
                        <li key={i} className={
                          r.status === "matched" ? "text-green-700" :
                          r.status === "not_found" ? "text-red-700" :
                          "text-amber-700"
                        }>
                          {r.status === "matched" && `✓ ${r.filename} → ${r.code} (${r.name})`}
                          {r.status === "not_found" && `✗ ${r.filename} — no existe producto con código ${r.code}`}
                          {r.status === "too_big" && `⚠ ${r.filename} excede 5MB`}
                          {r.status === "invalid" && `⚠ ${r.filename} — ${r.reason || "inválida"}`}
                        </li>
                      ))}
                      {bulkImagesResult.rows.length > 30 && (
                        <li className="text-slate-500">... y {bulkImagesResult.rows.length - 30} más</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import History Dialog (Master only) */}
      <Dialog open={showImportHistory} onOpenChange={setShowImportHistory}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Historial de Cargas de Excel
            </DialogTitle>
            <DialogDescription>
              Cada fila representa una carga del archivo Excel. Puedes descargar el reporte detallado o revertir una carga si tiene errores.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto" data-testid="import-history-list">
            {batchesLoading ? (
              <p className="text-center py-8 text-slate-500">Cargando historial...</p>
            ) : importBatches.length === 0 ? (
              <p className="text-center py-8 text-slate-500">No hay cargas registradas para esta caja.</p>
            ) : (
              <div className="space-y-2">
                {importBatches.map((b) => {
                  const dt = b.created_at ? new Date(b.created_at).toLocaleString("es-CR") : "";
                  const reverted = !!b.reverted;
                  return (
                    <div
                      key={b.id}
                      data-testid={`import-batch-row-${b.id}`}
                      className={`p-3 border rounded-lg flex items-start justify-between gap-3 ${reverted ? "bg-slate-50 border-slate-200 opacity-70" : "bg-white border-slate-200"}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-800 truncate">{b.filename}</span>
                          {reverted && (
                            <span className="text-[10px] uppercase tracking-wide bg-red-100 text-red-700 px-2 py-0.5 rounded">Revertida</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {dt} · {b.created_by}
                        </div>
                        <div className="text-xs mt-2 flex gap-3 flex-wrap">
                          <span className="text-green-700">+{b.total_imported || 0} nuevos</span>
                          <span className="text-blue-700">~{b.total_updated || 0} actualizados</span>
                          <span className="text-slate-700">Total compra: ₡{(b.total_amount || 0).toLocaleString("es-CR")}</span>
                        </div>
                        {reverted && (
                          <div className="text-[11px] text-red-600 mt-1">
                            Revertida {b.reverted_at ? `el ${new Date(b.reverted_at).toLocaleString("es-CR")}` : ""}
                            {b.reverted_by ? ` por ${b.reverted_by}` : ""}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={batchActionId === b.id}
                          onClick={() => downloadBatchReport(b.id, b.filename)}
                          data-testid={`batch-report-btn-${b.id}`}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Reporte
                        </Button>
                        {!reverted && (
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={batchActionId === b.id}
                            onClick={() => revertBatch(b)}
                            data-testid={`batch-revert-btn-${b.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Eliminar
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuración de Facturación
            </DialogTitle>
            <DialogDescription>
              Configure los datos de su negocio y credenciales de Hacienda (opcionales)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Business Info */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">📋 Datos del Negocio</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nombre del Negocio</Label>
                  <Input 
                    value={invoiceSettings.business_name} 
                    onChange={(e) => setInvoiceSettings(prev => ({...prev, business_name: e.target.value}))}
                    placeholder="Mi Negocio S.A."
                  />
                </div>
                <div>
                  <Label>Cédula Jurídica/Física</Label>
                  <Input 
                    value={invoiceSettings.business_id} 
                    onChange={(e) => setInvoiceSettings(prev => ({...prev, business_id: e.target.value}))}
                    placeholder="3-101-123456"
                  />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input 
                    value={invoiceSettings.business_phone} 
                    onChange={(e) => setInvoiceSettings(prev => ({...prev, business_phone: e.target.value}))}
                    placeholder="8888-8888"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Dirección</Label>
                  <Input 
                    value={invoiceSettings.business_address} 
                    onChange={(e) => setInvoiceSettings(prev => ({...prev, business_address: e.target.value}))}
                    placeholder="San José, Costa Rica"
                  />
                </div>
              </div>
            </div>

            {/* Hacienda Credentials */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-amber-800">🔐 Credenciales de Hacienda (Opcional)</h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={invoiceSettings.enabled}
                    onChange={(e) => setInvoiceSettings(prev => ({...prev, enabled: e.target.checked}))}
                    className="rounded"
                  />
                  <span className="text-sm text-amber-700">Habilitar facturación electrónica</span>
                </label>
              </div>
              <p className="text-xs text-amber-600 mb-3">
                Para facturación electrónica oficial, necesita las credenciales del ATV de Hacienda.
                Sin estas credenciales, los documentos se generarán sin validez fiscal.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Usuario ATV</Label>
                  <Input 
                    value={invoiceSettings.atv_user || ""} 
                    onChange={(e) => setInvoiceSettings(prev => ({...prev, atv_user: e.target.value}))}
                    placeholder="usuario@hacienda"
                    disabled={!invoiceSettings.enabled}
                  />
                </div>
                <div>
                  <Label>Contraseña ATV</Label>
                  <Input 
                    type="password"
                    value={invoiceSettings.atv_password || ""} 
                    onChange={(e) => setInvoiceSettings(prev => ({...prev, atv_password: e.target.value}))}
                    placeholder="********"
                    disabled={!invoiceSettings.enabled}
                  />
                </div>
                <div className="col-span-2">
                  <Label>PIN del Certificado Digital (.p12)</Label>
                  <Input 
                    type="password"
                    value={invoiceSettings.certificate_pin || ""} 
                    onChange={(e) => setInvoiceSettings(prev => ({...prev, certificate_pin: e.target.value}))}
                    placeholder="****"
                    disabled={!invoiceSettings.enabled}
                  />
                </div>
                {invoiceSettings.enabled && invoiceSettings.atv_user && (
                  <div className="col-span-2 pt-2">
                    <Button 
                      variant="outline" 
                      onClick={testHaciendaCredentials}
                      className="w-full"
                      disabled={!invoiceSettings.atv_password}
                    >
                      🔗 Probar Conexión con Hacienda
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Cancelar</Button>
            <Button onClick={saveInvoiceSettings} className="bg-blue-600 hover:bg-blue-700">
              Guardar Configuración
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Preview Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Comprobante Generado
            </DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg p-2 bg-white max-h-96 overflow-y-auto">
            <iframe
              title="print-preview"
              srcDoc={printHtml}
              className="w-full"
              style={{ height: '360px', border: 'none' }}
              sandbox="allow-same-origin"
            />
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPrintDialog(false)}>Cerrar</Button>
            <Button
              onClick={() => sendInvoiceWhatsApp(lastSaleReceipt?.sale_id || lastSaleId)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="invoice-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Enviar por WhatsApp
            </Button>
            <Button onClick={openPrintWindow} className="bg-green-600 hover:bg-green-700">
              <Printer className="w-4 h-4 mr-2" />
              Imprimir (80mm)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SINPE / Transferencia - Comprobante number prompt */}
      <Dialog open={showPaymentRefDialog} onOpenChange={setShowPaymentRefDialog}>
        <DialogContent className="max-w-sm" data-testid="payment-ref-dialog">
          <DialogHeader>
            <DialogTitle>
              Nº comprobante {paymentMethod === "sinpe" ? "SINPE" : "transferencia"}
            </DialogTitle>
            <DialogDescription>
              Ingresá el número del comprobante recibido. Es solo para registro — <b>no afecta caja ni monto esperado</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-sm">Número de comprobante / referencia</Label>
            <Input
              autoFocus
              value={paymentRefInput}
              onChange={(e) => setPaymentRefInput(e.target.value)}
              placeholder={paymentMethod === "sinpe" ? "Ej: 123456789" : "Ej: TRX-987654"}
              data-testid="payment-ref-input"
              onKeyDown={(e) => {
                if (e.key === "Enter" && paymentRefInput.trim()) {
                  setPaymentReference(paymentRefInput.trim());
                  setShowPaymentRefDialog(false);
                  setTimeout(() => handleSale(), 50);
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentRefDialog(false)}>Cancelar</Button>
            <Button
              disabled={!paymentRefInput.trim()}
              onClick={() => {
                setPaymentReference(paymentRefInput.trim());
                setShowPaymentRefDialog(false);
                setTimeout(() => handleSale(), 50);
              }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="payment-ref-confirm"
            >
              Registrar y facturar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cash Payment Dialog — muestra "Paga con" + Vuelto en grande */}
      <Dialog open={showCashDialog} onOpenChange={setShowCashDialog}>
        <DialogContent className="max-w-md" data-testid="cash-payment-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">💵 Pago en Efectivo</DialogTitle>
            <DialogDescription>
              Ingresá el monto con el que el cliente paga y confirmá la venta.
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const rec = parseFloat(cashDialogInput) || 0;
            const change = rec - cartTotal;
            const isEnough = rec >= cartTotal;
            // Sugerencias: exacto + próximos billetes redondeados
            const uniqueAmts = new Set();
            uniqueAmts.add(cartTotal);
            for (const bill of [1000, 2000, 5000, 10000, 20000, 50000]) {
              const rounded = Math.ceil(cartTotal / bill) * bill;
              if (rounded > cartTotal) uniqueAmts.add(rounded);
            }
            const sortedQuick = Array.from(uniqueAmts).sort((a, b) => a - b).slice(0, 6);
            const confirm = () => {
              if (!isEnough) { toast.error("El monto recibido es menor al total"); return; }
              setCashReceived(String(rec));
              setShowCashDialog(false);
              setTimeout(() => handleSale(), 50);
            };
            return (
              <div className="space-y-4">
                <div className="bg-slate-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Total a pagar</p>
                  <p className="text-3xl font-bold text-slate-800">₡{cartTotal.toLocaleString("es-CR")}</p>
                </div>
                <div>
                  <Label className="text-base font-semibold">Paga con (₡)</Label>
                  <Input
                    type="number"
                    autoFocus
                    value={cashDialogInput}
                    onChange={(e) => setCashDialogInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && rec >= cartTotal) confirm(); }}
                    placeholder="Ej: 20000"
                    className="text-3xl h-16 font-bold text-center"
                    data-testid="cash-dialog-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {sortedQuick.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => setCashDialogInput(String(amt))}
                      className={`text-sm px-2 py-2 rounded border font-medium ${
                        Number(cashDialogInput) === amt
                          ? "bg-blue-600 text-white border-blue-700"
                          : "bg-white text-blue-700 border-blue-300 hover:bg-blue-50"
                      }`}
                      data-testid={`cash-dialog-quick-${amt}`}
                    >
                      {amt === cartTotal ? "Exacto" : `₡${amt.toLocaleString()}`}
                    </button>
                  ))}
                </div>
                {rec > 0 && (
                  <div
                    className={`rounded-lg p-4 text-center border-2 ${
                      isEnough
                        ? "bg-emerald-50 border-emerald-400"
                        : "bg-red-50 border-red-400"
                    }`}
                    data-testid="cash-dialog-change"
                  >
                    {isEnough ? (
                      <>
                        <p className="text-sm text-emerald-700 font-semibold uppercase">Vuelto al cliente</p>
                        <p className="text-5xl font-bold text-emerald-800 mt-1">
                          ₡{change.toLocaleString("es-CR", { maximumFractionDigits: 0 })}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-red-700 font-semibold uppercase">Faltan</p>
                        <p className="text-4xl font-bold text-red-800 mt-1">
                          ₡{Math.abs(change).toLocaleString("es-CR", { maximumFractionDigits: 0 })}
                        </p>
                      </>
                    )}
                  </div>
                )}
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setShowCashDialog(false)} data-testid="cash-dialog-cancel">
                    Cancelar
                  </Button>
                  <Button
                    disabled={!isEnough}
                    onClick={confirm}
                    className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                    data-testid="cash-dialog-confirm"
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" /> Confirmar y Facturar
                  </Button>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
      <Dialog open={showVoidDialog} onOpenChange={setShowVoidDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Anular Venta
            </DialogTitle>
            <DialogDescription>
              Esta acción restaurará el inventario y marcará la venta como anulada.
            </DialogDescription>
          </DialogHeader>
          {saleToVoid && (
            <div className="space-y-4">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm"><strong>Venta:</strong> {saleToVoid.id?.slice(0, 8)}...</p>
                <p className="text-sm"><strong>Total:</strong> ₡{saleToVoid.total?.toLocaleString()}</p>
                <p className="text-sm"><strong>Fecha:</strong> {new Date(saleToVoid.created_at).toLocaleString()}</p>
              </div>
              
              <div>
                <Label>Razón de Anulación</Label>
                <Input 
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  placeholder="Ej: Error en precio, devolución, etc."
                />
              </div>
              
              <div>
                <Label>Contraseña MASTER *</Label>
                <Input 
                  type="password"
                  value={voidPassword}
                  onChange={(e) => setVoidPassword(e.target.value)}
                  placeholder="Ingrese contraseña"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowVoidDialog(false); setSaleToVoid(null); }}>
              Cancelar
            </Button>
            <Button onClick={handleVoidSale} className="bg-red-600 hover:bg-red-700">
              <XCircle className="w-4 h-4 mr-2" />
              Confirmar Anulación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enlarged Image Modal */}
      {enlargedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setEnlargedImage(null)}
          data-testid="enlarged-image-modal"
        >
          <div className="relative max-w-3xl max-h-[85vh] w-full" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setEnlargedImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-slate-300 text-sm flex items-center gap-1"
              data-testid="close-enlarged-image"
            >
              <XCircle className="w-5 h-5" /> Cerrar
            </button>
            <img 
              src={enlargedImage.src} 
              alt={enlargedImage.name}
              className="w-full h-full object-contain rounded-lg"
            />
            <p className="text-white text-center mt-2 text-sm font-medium">{enlargedImage.name}</p>
          </div>
        </div>
      )}

      {/* IMPORT PREVIEW DIALOG: shown after the user picks an Excel file but
          before any DB writes. Lists every row that will be created or updated.
          If any errors exist the confirm button is disabled (user must fix the
          Excel first). */}
      <Dialog open={!!importPreview} onOpenChange={(o) => !o && handleImportCancel()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Vista previa de importación</DialogTitle>
            <DialogDescription>
              Revisa los productos que se cargarán o actualizarán. Nada se guardará hasta que confirmes.
            </DialogDescription>
          </DialogHeader>
          {importPreview && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="rounded-md border border-green-300 bg-green-50 p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">{importPreview.imported}</div>
                  <div className="text-xs text-green-700">Nuevos</div>
                </div>
                <div className="rounded-md border border-blue-300 bg-blue-50 p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700">{importPreview.updated}</div>
                  <div className="text-xs text-blue-700">A actualizar</div>
                </div>
                <div className={`rounded-md border p-3 text-center ${(importPreview.errors||[]).length ? "border-red-300 bg-red-50" : "border-slate-200 bg-slate-50"}`}>
                  <div className={`text-2xl font-bold ${(importPreview.errors||[]).length ? "text-red-700" : "text-slate-500"}`}>{(importPreview.errors||[]).length}</div>
                  <div className="text-xs">Errores</div>
                </div>
              </div>
              {(importPreview.errors || []).length > 0 && (
                <div className="rounded-md border border-red-300 bg-red-50 p-3 mb-3 text-sm">
                  <div className="font-bold text-red-700 mb-1">Errores que bloquean la carga:</div>
                  <ul className="list-disc list-inside text-red-700 space-y-0.5">
                    {importPreview.errors.map((e, i) => <li key={i}>{typeof e === "string" ? e : JSON.stringify(e)}</li>)}
                  </ul>
                </div>
              )}
              <div className="overflow-auto flex-1 border rounded-md">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-100">
                    <tr>
                      <th className="p-2 text-left">Acción</th>
                      <th className="p-2 text-left">Código</th>
                      <th className="p-2 text-left">Nombre</th>
                      <th className="p-2 text-right">Precio</th>
                      <th className="p-2 text-right">Cantidad</th>
                      <th className="p-2 text-right">Costo</th>
                      <th className="p-2 text-left">Categoría</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(importPreview.preview_rows || []).map((r, i) => (
                      <tr key={i} className={`border-t ${r.status === "new" ? "bg-green-50" : "bg-blue-50"}`}>
                        <td className="p-2">
                          {r.status === "new" ? (
                            <span className="text-xs font-bold text-green-700 bg-green-200 px-2 py-0.5 rounded">NUEVO</span>
                          ) : (
                            <span className="text-xs font-bold text-blue-700 bg-blue-200 px-2 py-0.5 rounded">ACTUALIZAR</span>
                          )}
                        </td>
                        <td className="p-2 font-mono text-xs">{r.code}</td>
                        <td className="p-2">{r.name}</td>
                        <td className="p-2 text-right font-mono">
                          {r.prev_price !== undefined && r.prev_price !== null && r.prev_price !== r.price && (
                            <span className="text-xs text-slate-400 line-through mr-1">₡{Number(r.prev_price).toLocaleString()}</span>
                          )}
                          ₡{Number(r.price || 0).toLocaleString()}
                        </td>
                        <td className="p-2 text-right font-mono">
                          {r.prev_qty !== undefined && r.prev_qty !== null && r.prev_qty !== r.qty && (
                            <span className="text-xs text-slate-400 line-through mr-1">{r.prev_qty}</span>
                          )}
                          {r.qty ?? "—"}
                        </td>
                        <td className="p-2 text-right font-mono">₡{Number(r.cost || 0).toLocaleString()}</td>
                        <td className="p-2 text-xs">{r.category || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={handleImportCancel} disabled={importCommitLoading} data-testid="import-preview-cancel-btn">
              Cancelar
            </Button>
            <Button
              onClick={handleImportConfirm}
              disabled={importCommitLoading || (importPreview && (importPreview.errors || []).length > 0)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="import-preview-confirm-btn"
            >
              {importCommitLoading ? "Cargando..." : "Cargar al sistema"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BULK IMAGES PREVIEW DIALOG: same two-step flow for bulk image uploads */}
      <Dialog open={!!bulkImagesPreview} onOpenChange={(o) => !o && handleBulkImagesCancel()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Vista previa de carga de imágenes</DialogTitle>
            <DialogDescription>
              Revisa qué imágenes se asignarán a productos. Nada se guardará hasta que confirmes.
            </DialogDescription>
          </DialogHeader>
          {bulkImagesPreview && (() => {
            const matched = bulkImagesPreview.matched || 0;
            const notFound = bulkImagesPreview.not_found || 0;
            const invalid = bulkImagesPreview.invalid || 0;
            const tooBig = bulkImagesPreview.too_big || 0;
            const blockCount = notFound + invalid + tooBig;
            return (
              <>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <div className="rounded-md border border-green-300 bg-green-50 p-3 text-center">
                    <div className="text-2xl font-bold text-green-700">{matched}</div>
                    <div className="text-xs text-green-700">Asignables</div>
                  </div>
                  <div className={`rounded-md border p-3 text-center ${notFound ? "border-red-300 bg-red-50" : "border-slate-200 bg-slate-50"}`}>
                    <div className={`text-2xl font-bold ${notFound ? "text-red-700" : "text-slate-500"}`}>{notFound}</div>
                    <div className="text-xs">Sin producto</div>
                  </div>
                  <div className={`rounded-md border p-3 text-center ${invalid ? "border-red-300 bg-red-50" : "border-slate-200 bg-slate-50"}`}>
                    <div className={`text-2xl font-bold ${invalid ? "text-red-700" : "text-slate-500"}`}>{invalid}</div>
                    <div className="text-xs">Inválidas</div>
                  </div>
                  <div className={`rounded-md border p-3 text-center ${tooBig ? "border-red-300 bg-red-50" : "border-slate-200 bg-slate-50"}`}>
                    <div className={`text-2xl font-bold ${tooBig ? "text-red-700" : "text-slate-500"}`}>{tooBig}</div>
                    <div className="text-xs">Muy grandes</div>
                  </div>
                </div>
                {blockCount > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 mb-3 text-sm text-amber-800">
                    Hay {blockCount} archivo(s) con problemas. Al cargar se <strong>omitirán</strong> y solo se asignarán las {matched} válidas.
                  </div>
                )}
                <div className="overflow-auto flex-1 border rounded-md">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-100">
                      <tr>
                        <th className="p-2 text-left">Archivo</th>
                        <th className="p-2 text-left">Código detectado</th>
                        <th className="p-2 text-left">Estado</th>
                        <th className="p-2 text-left">Producto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(bulkImagesPreview.rows || []).map((r, i) => (
                        <tr key={i} className={`border-t ${r.status === "matched" ? "bg-green-50" : "bg-red-50"}`}>
                          <td className="p-2 text-xs font-mono">{r.filename}</td>
                          <td className="p-2 text-xs font-mono">{r.code}</td>
                          <td className="p-2 text-xs">
                            {r.status === "matched" && (
                              <span className="text-green-700 font-bold">
                                ✓ {r.matched_count > 1 ? `${r.matched_count} productos` : "asignable"}
                              </span>
                            )}
                            {r.status === "not_found" && <span className="text-red-700">sin producto</span>}
                            {r.status === "invalid" && <span className="text-red-700">inválida</span>}
                            {r.status === "too_big" && <span className="text-red-700">muy grande</span>}
                          </td>
                          <td className="p-2">{r.name || (r.reason ? <span className="text-xs text-slate-500">{r.reason}</span> : "—")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <DialogFooter className="mt-3">
                  <Button variant="outline" onClick={handleBulkImagesCancel} disabled={bulkImagesCommitLoading}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleBulkImagesConfirm}
                    disabled={bulkImagesCommitLoading || matched === 0}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="bulk-images-confirm-btn"
                  >
                    {bulkImagesCommitLoading ? "Subiendo..." : `Cargar ${matched} imagen(es)`}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
